import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supplyChainService } from '../services/apiClient';
import { calculateDailyRunRates, calculateDemandGrowthRate, calculateReorderLogic, RawSalesRecord } from '../utils/inventoryCalculations';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import {
  useOrdersInProgress,
  useShipments,
  useCreateOrder,
  useDeleteOrder,
  useUpdateShipment,
  useDeleteShipment,
  queryKeys
} from '../hooks/useInventoryQueries';





// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface OrderToBePlaced {
  product_name: string;
  country: string;
  quantity_needed: number;
  quantity_in_progress?: number;
  quantity_in_transit?: number;
  net_quantity_needed?: number;
  order_cutoff_date: string;
  days_until_cutoff: number;
  reorder_flagged: boolean;
  priority: string;
  manufacturing_time: number;
  estimated_cost: number;
  current_stock: number;
  daily_run_rate: number;
  countries_details?: any;
}

// Grouped order by product (for consolidated view)
interface GroupedOrder {
  product_name: string;
  total_quantity_needed: number;
  total_estimated_cost: number;
  earliest_cutoff_date: string;
  earliest_days_until_cutoff: number;
  orders_within_30_days: number;
  priority: string;
  unit_cost: number;
  country_breakdown: OrderToBePlaced[];
}

// Unit costs per product - matching backend config
const PRODUCT_UNIT_COSTS: Record<string, number> = {
  "NHH": 1.60,
  "ARR Series": 1.40,
  "ADRQ Series": 1.00,
  "ARP Series": 1.00,
  "RG Series": 1.00,
  "Marketing Materials": 0.50,
};

const getUnitCost = (productName: string): number => {
  return PRODUCT_UNIT_COSTS[productName] || 1.00;
};

// Product to SKU mapping for auto-fill
const PRODUCT_SKU_MAPPING: Record<string, string> = {
  "NHH": "NHH",
  "ARR Series": "ARR",
  "ADRQ Series": "ADRQ",
  "ARP Series": "ARP",
  "RG Series": "RG",
  "Marketing Materials": "MKT",
};

// Reverse mapping: SKU prefix to Product Name (for matching shipments to products)
const SKU_TO_PRODUCT_MAPPING: Record<string, string> = {
  "NHH": "NHH",
  "ARR": "ARR Series",
  "ADRQ": "ADRQ Series",
  "ARP": "ARP Series",
  "RG": "RG Series",
  "MKT": "Marketing Materials",
  "brochure": "Marketing Materials",
};

// Helper function to get product name from SKU
const getProductNameFromSku = (sku: string): string | null => {
  if (!sku) return null;

  // Try exact match first
  for (const [productName, skuPrefix] of Object.entries(PRODUCT_SKU_MAPPING)) {
    if (sku.toUpperCase() === skuPrefix.toUpperCase()) {
      return productName;
    }
  }

  // Try prefix match (e.g., "NHH" matches "NHH", "ARP-14D-L" matches "ARP")
  const skuUpper = sku.toUpperCase();
  for (const [skuPrefix, productName] of Object.entries(SKU_TO_PRODUCT_MAPPING)) {
    if (skuUpper.startsWith(skuPrefix.toUpperCase())) {
      return productName;
    }
  }

  // Special cases for known SKU patterns
  if (skuUpper.includes("NHH")) return "NHH";
  if (skuUpper.includes("ARR")) return "ARR Series";
  if (skuUpper.includes("ADRQ")) return "ADRQ Series";
  if (skuUpper.includes("ARP")) return "ARP Series";
  if (skuUpper.includes("RG")) return "RG Series";
  if (skuUpper.includes("BROCHURE") || skuUpper.includes("MKT")) return "Marketing Materials";

  return null;
};

// Helper function to normalize warehouse/country names for matching
const normalizeWarehouseName = (warehouse: string): string => {
  const normalized = warehouse.toUpperCase().trim();
  // Map common variations
  if (normalized === "UNITED STATES" || normalized === "US") return "US";
  if (normalized === "UNITED KINGDOM" || normalized === "UK" || normalized === "GB") return "UK";
  if (normalized === "CANADA" || normalized === "CA") return "CA";
  if (normalized === "AUSTRALIA" || normalized === "AU") return "AU";
  return normalized;
};

// Available products list for dropdown
const AVAILABLE_PRODUCTS = [
  { name: "NHH", sku: "NHH", description: "Natural Healing Handbook" },
  { name: "ARR Series", sku: "ARR", description: "ARR Hardcover & Recipe Cards" },
  { name: "ADRQ Series", sku: "ADRQ", description: "Ancient Remedies Products" },
  { name: "ARP Series", sku: "ARP", description: "Supplement Series" },
  { name: "RG Series", sku: "RG", description: "China Brain/Gut/Heart Supplements" },
  { name: "Marketing Materials", sku: "MKT", description: "Brochures and Marketing" },
];

// Manufacturer codes
const MANUFACTURERS = [
  { code: "CN1", name: "China Manufacturer 1 (Primary)" },
  { code: "CN2", name: "China Manufacturer 2" },
  { code: "VN1", name: "Vietnam Manufacturer 1" },
  { code: "IN1", name: "India Manufacturer 1" },
];

// Generate order number preview: CN1-120225-XX (Manufacturer-MMDDYY-Sequence)
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                // Note: Actual order number is generated by backend for uniqueness
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                const generateOrderNumberPreview = (manufacturer: string, datePlaced: string): string => {
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  // Parse date and format as MMDDYY
                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                                  const date = new Date(datePlaced);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  const dateStr = `${mm}${dd}${yy}`;

  // Show preview with XX as placeholder for sequence (backend determines actual sequence)
  return `${manufacturer}-${dateStr}-XX`;
};

interface OrderInProgress {
  order_id: number;
  order_number: string;
  product_name: string;
  sku: string;
  quantity: number;
  date_ordered: string;  // API returns date_ordered
  total_cost: number | null;  // Can be null
  status: string;
  allocations: OrderAllocation[];
  shipments_count?: number;
}

// API response format - uses final_quantity
interface OrderAllocation {
  allocation_id?: number;
  warehouse: string;
  final_quantity: number;
  recommended_quantity?: number;
  is_locked?: boolean;
  shipped_quantity?: number;
  remaining_quantity?: number;
}

// Form input format - uses quantity for simpler UI binding
interface FormAllocation {
  allocation_id?: number;
  warehouse: string;
  quantity: number | string;
  estimated_arrival?: string;
  recommended_quantity?: number;
  is_locked?: boolean;
  shipped_quantity?: number;
  remaining_quantity?: number;
}

interface TrackingEntry {
  tracking_number: string;
  tracking_url: string | null;
  cartons_count: number | null;
}

interface LightDropdownOption {
  value: string;
  label: string;
}

interface LightDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: LightDropdownOption[];
  placeholder?: string;
}

const LightDropdown: React.FC<LightDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selected = options.find((option) => option.value === value);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-left flex items-center justify-between focus:outline-none focus:ring-0 focus:border-blue-500"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-500'}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1">
          {options.map((option) => (
            <button
              key={`light-dropdown-${option.value || 'empty'}`}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-sm text-left transition-colors flex items-center justify-between ${
                value === option.value
                  ? 'bg-blue-50 text-blue-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option.label}
              {value === option.value && (
                <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Shipment interface for tracking
interface Shipment {
  shipment_id: number;
  order_id: number;
  order_number: string;
  sku: string;
  quantity: number;
  origin: string;
  destination: string;
  status: string;
  estimated_ship_date: string | null;
  estimated_arrival_date: string | null;
  actual_ship_date: string | null;
  actual_arrival_date: string | null;
  supplier: string | null;
  shipping_method: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  tracking_entries?: TrackingEntry[];
  shipping_reference: string | null;
  notes: string | null;
  created_at: string;
}


const UnifiedOrderManagement: React.FC = () => {
  // Use shared toast context
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const queryClient = useQueryClient();

  // ============================================================================
  // REACT QUERY HOOKS (with automatic caching)
  // ============================================================================

  // Orders In Progress - cached, auto-refreshes
  const {
    data: ordersInProgressData,
    isLoading: loadingOrdersQuery,
    refetch: refetchOrders
  } = useOrdersInProgress();

  // Shipments - cached, auto-refreshes
  const {
    data: shipmentsData,
    isLoading: loadingShipmentsQuery,
    refetch: refetchShipments
  } = useShipments();

  // Mutations with automatic cache invalidation
  const createOrderMutation = useCreateOrder();
  const deleteOrderMutation = useDeleteOrder();
  const updateShipmentMutation = useUpdateShipment();
  const deleteShipmentMutation = useDeleteShipment();

  // Convert query data to component state format
  // API returns { orders: [...] } so we extract the array
  // Columns that are nullable in the database are defaulted here so the table
  // renders blanks rather than "null".
  const ordersInProgress: OrderInProgress[] = useMemo(() => {
    if (!ordersInProgressData) return [];
    return (ordersInProgressData.orders || []).map((order) => ({
      ...order,
      product_name: order.product_name ?? "",
      date_ordered: order.date_ordered ?? "",
      status: order.status ?? "",
    }));
  }, [ordersInProgressData]);

  // API returns { shipments: [...] } so we extract the array
  const shipments: Shipment[] = useMemo(() => {
    if (!shipmentsData) return [];
    return (shipmentsData.shipments || []).map((shipment) => ({
      ...shipment,
      order_number: shipment.order_number ?? "",
      origin: shipment.origin ?? "",
      status: shipment.status ?? "",
      created_at: shipment.created_at ?? "",
    }));
  }, [shipmentsData]);

  // Combined loading state for Supabase data
  const loadingOrders = loadingOrdersQuery || loadingShipmentsQuery;

  // Tab state
  const [activeTab, setActiveTab] = useState<'orders-to-place' | 'in-progress' | 'shipments'>('orders-to-place');
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState('');
  const [activeShipmentFilter, setActiveShipmentFilter] = useState<'all' | 'pending' | 'in_transit' | 'customs' | 'delivered'>('all');

  // Orders to place state (from BigQuery - still needs manual fetch)
  const [ordersToPlace, setOrdersToPlace] = useState<OrderToBePlaced[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Group orders by product for consolidated view
  const groupOrdersByProduct = (orders: OrderToBePlaced[]): GroupedOrder[] => {
    const grouped: Record<string, GroupedOrder> = {};

    orders.forEach(order => {
      const productName = order.product_name;

      if (!grouped[productName]) {
        grouped[productName] = {
          product_name: productName,
          total_quantity_needed: 0,
          total_estimated_cost: 0,
          earliest_cutoff_date: order.order_cutoff_date,
          earliest_days_until_cutoff: order.days_until_cutoff,
          orders_within_30_days: 0,
          priority: order.priority,
          unit_cost: getUnitCost(productName),
          country_breakdown: []
        };
      }

      const group = grouped[productName];
      const netQty = order.net_quantity_needed ?? order.quantity_needed;

      group.total_quantity_needed += netQty;
      group.total_estimated_cost += netQty * group.unit_cost;
      group.country_breakdown.push(order);

      // Track earliest cutoff
      if (order.days_until_cutoff < group.earliest_days_until_cutoff) {
        group.earliest_cutoff_date = order.order_cutoff_date;
        group.earliest_days_until_cutoff = order.days_until_cutoff;
        group.priority = order.priority;
      }
    });

    // Calculate orders within 30 days of earliest cutoff for each product
    Object.values(grouped).forEach(group => {
      const earliestDays = group.earliest_days_until_cutoff;
      group.orders_within_30_days = group.country_breakdown.filter(
        o => o.days_until_cutoff <= earliestDays + 30
      ).length;

      // Sort country breakdown by urgency
      group.country_breakdown.sort((a, b) => a.days_until_cutoff - b.days_until_cutoff);
    });

    // Sort groups by earliest cutoff
    return Object.values(grouped).sort((a, b) => a.earliest_days_until_cutoff - b.earliest_days_until_cutoff);
  };

  const groupedOrders = groupOrdersByProduct(ordersToPlace);

  const toggleProductExpand = (productName: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productName)) {
        newSet.delete(productName);
      } else {
        newSet.add(productName);
      }
      return newSet;
    });
  };

  // Toggle order number expansion for shipments grouping
  const toggleOrderExpand = (orderNumber: string) => {
    setExpandedOrderNumbers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderNumber)) {
        newSet.delete(orderNumber);
      } else {
        newSet.add(orderNumber);
      }
      return newSet;
    });
  };

  const filteredShipments = useMemo(() => {
    const query = shipmentSearchQuery.trim().toLowerCase();
    return shipments.filter((shipment) => {
      const matchesStatus =
        activeShipmentFilter === 'all' ||
        (activeShipmentFilter === 'pending' && (shipment.status === 'pending_details' || shipment.status === 'pending_ship')) ||
        (activeShipmentFilter === 'in_transit' && shipment.status === 'in_transit') ||
        (activeShipmentFilter === 'customs' && shipment.status === 'customs_clearance') ||
        (activeShipmentFilter === 'delivered' && (shipment.status === 'arrived_at_warehouse' || shipment.status === 'complete'));

      if (!matchesStatus) return false;
      if (!query) return true;

      const trackingFromEntries = (shipment.tracking_entries || [])
        .map((entry) => `${entry?.tracking_number || ''} ${entry?.tracking_url || ''}`)
        .join(' ')
        .toLowerCase();

      const searchable = [
        shipment.order_number || '',
        shipment.sku || '',
        shipment.shipping_reference || '',
        shipment.tracking_number || '',
        shipment.tracking_url || '',
        trackingFromEntries
      ].join(' ').toLowerCase();

      return searchable.includes(query);
    });
  }, [shipments, activeShipmentFilter, shipmentSearchQuery]);

  const shipmentStats = useMemo(() => {
    let pending = 0;
    let inTransit = 0;
    let customs = 0;
    let delivered = 0;

    for (const shipment of shipments) {
      if (shipment.status === 'pending_details' || shipment.status === 'pending_ship') pending += 1;
      if (shipment.status === 'in_transit') inTransit += 1;
      if (shipment.status === 'customs_clearance') customs += 1;
      if (shipment.status === 'arrived_at_warehouse' || shipment.status === 'complete') delivered += 1;
    }

    return {
      total: shipments.length,
      pending,
      inTransit,
      customs,
      delivered,
    };
  }, [shipments]);

  // Group shipments by order number
  const groupedShipments = useMemo(() => {
    const grouped: Record<string, Shipment[]> = {};

    filteredShipments.forEach(shipment => {
      const orderNum = shipment.order_number;
      if (!grouped[orderNum]) {
        grouped[orderNum] = [];
      }
      grouped[orderNum].push(shipment);
    });

    // Convert to array and sort by most recent first
    return Object.entries(grouped)
      .map(([orderNumber, shipmentsInOrder]) => {
        const orderId = shipmentsInOrder[0]?.order_id;

        // Calculate earliest ETA from all shipments
        const etas = shipmentsInOrder
          .map(s => s.estimated_arrival_date ? new Date(s.estimated_arrival_date).getTime() : null)
          .filter(eta => eta !== null) as number[];
        const earliestEta = etas.length > 0 ? new Date(Math.min(...etas)).toISOString() : null;

        return {
          orderNumber,
          order_id: orderId ?? null,
          shipments: shipmentsInOrder.sort((a, b) => b.shipment_id - a.shipment_id),
          totalQuantity: shipmentsInOrder.reduce((sum, s) => sum + s.quantity, 0),
          destinations: Array.from(new Set(shipmentsInOrder.map(s => s.destination))),
          statuses: Array.from(new Set(shipmentsInOrder.map(s => s.status))),
          sku: shipmentsInOrder[0]?.sku || '',
          supplier: shipmentsInOrder[0]?.supplier || '',
          created_at: shipmentsInOrder[0]?.created_at || '',
          earliest_eta: earliestEta  // Earliest shipment ETA for order header
        };
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [filteredShipments]);

  // Loading state
  const [error, setError] = useState<string | null>(null);

  // Shipment editing state
  const [expandedShipment, setExpandedShipment] = useState<number | null>(null);
  const [editingShipment, setEditingShipment] = useState<Shipment | null>(null);
  const [savingShipment, setSavingShipment] = useState(false);
  const [shipmentFormErrors, setShipmentFormErrors] = useState<string[]>([]);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [shippingMethodDropdownOpen, setShippingMethodDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const shippingMethodDropdownRef = useRef<HTMLDivElement>(null);

  // Shipment grouping state (by order number)
  const [expandedOrderNumbers, setExpandedOrderNumbers] = useState<Set<string>>(new Set());

  // Shipment change log state
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedShipmentForLog, setSelectedShipmentForLog] = useState<Shipment | null>(null);
  const [shipmentLogs, setShipmentLogs] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showAddShipmentForm, setShowAddShipmentForm] = useState(false);
  const [savingManualShipment, setSavingManualShipment] = useState(false);
  const [selectedShipmentOrderId, setSelectedShipmentOrderId] = useState<number | null>(null);
  const [newShipment, setNewShipment] = useState({
    destination: '',
    quantity: '' as string | number,
    estimated_ship_date: '',
    estimated_arrival_date: ''
  });

  // Manual order form state
  const [showAddOrderForm, setShowAddOrderForm] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [newOrder, setNewOrder] = useState({
    product_name: '',
    sku: '',
    manufacturer: 'CN1', // Default to China Manufacturer 1
    quantity: '' as string | number,
    date_placed: new Date().toISOString().split('T')[0],
    total_cost: '' as string | number,
    estimated_arrival: '',
    allocations: [] as FormAllocation[]
  });

  // ============================================================================
  // DATA FETCHING (BigQuery only - Supabase handled by React Query)
  // ============================================================================

  // BigQuery loading state (orders to place)
  const [loadingBigQuery, setLoadingBigQuery] = useState(activeTab === 'orders-to-place');

  // Track if BigQuery fetch has started
  const hasFetchedBigQuery = useRef(false);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setStatusDropdownOpen(false);
      }
      if (!shippingMethodDropdownRef.current) return;
      if (!shippingMethodDropdownRef.current.contains(event.target as Node)) {
        setShippingMethodDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Fetch BigQuery data only when orders-to-place tab is needed.
  useEffect(() => {
    if (activeTab !== 'orders-to-place') return;
    if (hasFetchedBigQuery.current) return;
    if (loadingOrdersQuery || loadingShipmentsQuery) {
      return;
    }

    hasFetchedBigQuery.current = true;
    void fetchBigQueryData();
  }, [activeTab, loadingOrdersQuery, loadingShipmentsQuery]);

  // Track last calculation to avoid redundant recalculations
  const lastCalculationRef = useRef<string>('');

  // Recalculate net quantities when shipments, ordersInProgress, or ordersToPlace change
  useEffect(() => {
    if (ordersToPlace.length > 0 && (shipments.length > 0 || ordersInProgress.length > 0)) {
      // Create a hash to check if recalculation is needed
      const calcHash = `${ordersToPlace.length}-${shipments.length}-${ordersInProgress.length}`;

      // Skip if already calculated with same data counts
      if (lastCalculationRef.current === calcHash) {
        return;
      }

      const adjustedOrders = calculateNetQuantities(ordersToPlace, ordersInProgress, shipments);
      const totalInProgress = adjustedOrders.reduce((sum, o) => sum + (o.quantity_in_progress ?? 0), 0);
      const totalInTransit = adjustedOrders.reduce((sum, o) => sum + (o.quantity_in_transit ?? 0), 0);

      // Only update if there are actual deductions to apply
      if (totalInProgress > 0 || totalInTransit > 0) {
        lastCalculationRef.current = calcHash;
        setOrdersToPlace(adjustedOrders);
      }
    }
  }, [shipments.length, ordersInProgress.length, ordersToPlace.length]); // Include ordersToPlace.length to trigger when BigQuery data loads

  // Refetch all data (manual refresh button)
  const refreshSupabaseData = async () => {
    // React Query handles cache invalidation - just refetch
    await Promise.all([refetchOrders(), refetchShipments()]);
  };

  const refreshOrderManagementQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.ordersInProgress() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.shipments() })
    ]);
  };

  // Fetch BigQuery data for orders to place
  const fetchBigQueryData = async (showLoader: boolean = true) => {
    if (showLoader) {
      setLoadingBigQuery(true);
    }
    setError(null);
    try {
      const ordersResponse = await supplyChainService.getOrdersToBePlaced();
      const processedOrders = processOrdersData(ordersResponse);

      // Use existing ordersInProgress and shipments state (already fetched by fetchSupabaseData)
      // Calculate net quantities (subtracts both in-progress orders AND in-transit shipments)
      const adjustedOrders = calculateNetQuantities(processedOrders, ordersInProgress, shipments);


      setOrdersToPlace(adjustedOrders);

      // Calculate summary
      const urgentCount = adjustedOrders.filter(o =>
        o.days_until_cutoff <= 7 || o.priority === 'urgent' || o.priority === 'critical'
      ).length;

      setSummary({
        total_orders: adjustedOrders.length,
        urgent_orders: urgentCount
      });
    } catch (err) {
      console.error('Error fetching BigQuery data:', err);
      setError('Failed to load orders to place');
    } finally {
      if (showLoader) {
        setLoadingBigQuery(false);
      }
    }
  };

  const fetchData = async () => {
    await Promise.all([refreshSupabaseData(), fetchBigQueryData()]);
  };

  const processOrdersData = (rawData: any): OrderToBePlaced[] => {
    const orders: OrderToBePlaced[] = [];

    if (!rawData) {
      return orders;
    }

    // Preferred format from backend: compact orders list
    if (Array.isArray(rawData.orders)) {
      rawData.orders
        .filter((order: any) => Number(order.quantity_needed || 0) > 0)
        .forEach((order: any) => {
          orders.push({
            product_name: order.product_name || 'Unknown',
            country: order.country || 'Unknown',
            quantity_needed: Number(order.quantity_needed || 0),
            order_cutoff_date: order.order_cutoff_date || '',
            days_until_cutoff: Number(order.days_until_cutoff || 0),
            reorder_flagged: Boolean(order.reorder_flagged ?? true),
            priority: order.priority || 'normal',
            manufacturing_time: Number(order.manufacturing_time || 25),
            estimated_cost: Number(order.estimated_cost || 0),
            current_stock: Number(order.current_stock || 0),
            daily_run_rate: Number(order.daily_run_rate || 0),
            countries_details: order
          });
        });

      orders.sort((a, b) => a.days_until_cutoff - b.days_until_cutoff);
      return orders;
    }

    // Legacy format fallback
    if (!rawData.countries_data) {
      return orders;
    }

    // Process each country's data
    rawData.countries_data.forEach((countryData: any) => {
      const country = countryData.country;

      // Process each product in this country
      countryData.products?.forEach((product: any) => {
        // Use calculateProductMetrics to get EXACT SAME values as Central Stock
        // This ensures 100% consistency across all sections

        // Get inventory metrics
        const totalStock = product.raw_inventory_data?.reduce((sum: number, inv: any) =>
          sum + (inv.on_hand_quantity || 0), 0
        ) || 0;

        const allocatedStock = product.raw_inventory_data?.reduce((sum: number, inv: any) =>
          sum + (inv.allocated_quantity || 0), 0
        ) || 0;

        // Get sales records for DRR calculation
        let dailyRunRate = 0;
        let demandGrowthRate = 0;

        if (product.sku_sales_data && product.sku_sales_data.length > 0) {
          const allSalesRecords: RawSalesRecord[] = product.sku_sales_data.flatMap((skuData: any) =>
            (skuData.raw_sales_records || []).map((record: any) => ({
              date: record.date,
              sku: record.sku || skuData.sku_id,
              quantity: record.quantity || 0,
              country: record.country || country
            }))
          );

          if (allSalesRecords.length > 0) {
            // Use SAME calculation functions as Central Stock
            const runRates = calculateDailyRunRates(allSalesRecords);
            dailyRunRate = runRates.weighted_drr;

            // Also calculate growth rate (same as Central Stock)
            demandGrowthRate = calculateDemandGrowthRate(allSalesRecords);
          }
        }

        const manufacturingTime = product.manufacturing_time_days || 25;
        const shippingTime = product.shipping_time_days || 45;
        const safetyStockDays = product.safety_stock_days || 14;
        const reorderTiming = calculateReorderLogic(
          totalStock,
          allocatedStock,
          dailyRunRate,
          manufacturingTime,
          shippingTime,
          safetyStockDays,
          demandGrowthRate,
          0
        );
        const quantityNeeded = reorderTiming.minimum_reorder_amount;
        const daysUntilCutoff = reorderTiming.days_until_cutoff;

        // Determine if reorder is needed
        const needsReorder = reorderTiming.reorder_flagged && quantityNeeded > 0;

        if (needsReorder) {
          orders.push({
            product_name: product.product_name,
            country: country,
            quantity_needed: quantityNeeded,
            order_cutoff_date: reorderTiming.order_cutoff_date !== 'N/A'
              ? reorderTiming.order_cutoff_date
              : new Date(Date.now() + daysUntilCutoff * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            days_until_cutoff: daysUntilCutoff,
            reorder_flagged: true,
            priority: daysUntilCutoff <= 0 ? 'urgent' :
              daysUntilCutoff <= 7 ? 'high' :
                daysUntilCutoff <= 30 ? 'medium' : 'low',
            manufacturing_time: manufacturingTime,
            estimated_cost: quantityNeeded * getUnitCost(product.product_name),
            current_stock: totalStock,
            daily_run_rate: dailyRunRate,
            countries_details: product
          });
        }
      });
    });

    // Sort by urgency (days until cutoff)
    orders.sort((a, b) => a.days_until_cutoff - b.days_until_cutoff);

    return orders;
  };

  // Note: fetchOrdersInProgress and fetchShipments are now handled by React Query hooks
  // useOrdersInProgress() and useShipments() - with automatic caching!

  const calculateNetQuantities = (
    ordersToPlace: OrderToBePlaced[],
    ordersInProgress: OrderInProgress[],
    shipments: Shipment[] = []
  ): OrderToBePlaced[] => {
    return ordersToPlace.map(order => {
      // Find matching in-progress orders for this product + warehouse
      const matchingOrders = ordersInProgress.filter(inProgressOrder =>
        inProgressOrder.product_name === order.product_name &&
        inProgressOrder.allocations.some(alloc => alloc.warehouse === order.country)
      );

      // Use remaining (not-yet-shipped) qty — shipped units are counted as in-transit below
      const inProgressQty = matchingOrders.reduce((sum, inProgressOrder) => {
        const allocation = inProgressOrder.allocations.find(a => a.warehouse === order.country);
        if (!allocation) return sum;
        const remaining = typeof allocation.remaining_quantity === 'number'
          ? allocation.remaining_quantity
          : Math.max((allocation.final_quantity || 0) - (allocation.shipped_quantity || 0), 0);
        return sum + remaining;
      }, 0);

      // Find matching in-transit shipments for this product + warehouse
      const inTransitShipments = shipments.filter(shipment => {
        // Check if shipment is in transit
        if (shipment.status !== 'in_transit') return false;

        // Check if shipment SKU matches this product
        const shipmentProductName = getProductNameFromSku(shipment.sku);
        if (shipmentProductName !== order.product_name) {
          return false;
        }

        // Check if shipment destination matches order country
        const normalizedShipmentDest = normalizeWarehouseName(shipment.destination);
        const normalizedOrderCountry = normalizeWarehouseName(order.country);
        return normalizedShipmentDest === normalizedOrderCountry;
      });

      // Sum up quantities from in-transit shipments
      const inTransitQty = inTransitShipments.reduce((sum, shipment) => {
        return sum + (shipment.quantity || 0);
      }, 0);

      // Calculate net quantity needed (subtract both in-progress orders AND in-transit shipments)
      const totalDeducted = inProgressQty + inTransitQty;
      const netQty = Math.max(0, order.quantity_needed - totalDeducted);


      // This provides more accurate urgency by accounting for stock that's on the way
      const adjustedStock = (order.current_stock || 0) + inTransitQty + inProgressQty;
      const dailyRunRate = order.daily_run_rate || 0;

      const manufacturingTime = order.manufacturing_time || 25;
      const shippingTime = 45;
      const safetyStockDays = 14;
      const adjustedTiming = calculateReorderLogic(
        adjustedStock,
        0,
        dailyRunRate,
        manufacturingTime,
        shippingTime,
        safetyStockDays,
        0,
        0
      );
      const adjustedDaysUntilCutoff = adjustedTiming.days_until_cutoff;
      const adjustedCutoffDate = adjustedTiming.order_cutoff_date !== 'N/A'
        ? adjustedTiming.order_cutoff_date
        : new Date(Date.now() + adjustedDaysUntilCutoff * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      // Determine new priority based on adjusted cutoff
      const adjustedPriority: 'urgent' | 'high' | 'medium' | 'low' =
        adjustedDaysUntilCutoff <= 0 ? 'urgent' :
          adjustedDaysUntilCutoff <= 7 ? 'high' :
            adjustedDaysUntilCutoff <= 30 ? 'medium' : 'low';

      return {
        ...order,
        quantity_in_progress: inProgressQty,
        quantity_in_transit: inTransitQty, // Track in-transit separately
        net_quantity_needed: netQty,
        // Update cutoff fields with adjusted values
        days_until_cutoff: adjustedDaysUntilCutoff,
        order_cutoff_date: adjustedCutoffDate,
        priority: adjustedPriority
      };
    });
  };

  // ============================================================================
  // ORDER MANAGEMENT FUNCTIONS
  // ============================================================================

  const handleAddOrder = async () => {
    // Trim whitespace from text fields
    const productName = newOrder.product_name.trim();
    const sku = newOrder.sku.trim();
    const quantity = typeof newOrder.quantity === 'string' ? parseInt(newOrder.quantity) : newOrder.quantity;
    const totalCost = typeof newOrder.total_cost === 'string' ? parseFloat(newOrder.total_cost) : newOrder.total_cost;

    // Validation
    if (!productName) {
      showToast('error', 'Validation Error', 'Product Name is required and cannot be empty');
      return;
    }
    if (!sku) {
      showToast('error', 'Validation Error', 'SKU is required and cannot be empty');
      return;
    }
    if (!quantity || quantity <= 0 || isNaN(quantity)) {
      showToast('error', 'Validation Error', 'Quantity must be a valid number greater than 0');
      return;
    }
    if (!totalCost || totalCost <= 0 || isNaN(totalCost)) {
      showToast('error', 'Validation Error', 'Total Cost must be a valid number greater than 0');
      return;
    }
    if (newOrder.allocations.length === 0) {
      showToast('error', 'Validation Error', 'Please add at least one warehouse allocation');
      return;
    }

    // Validate all allocations have warehouse and quantity
    for (let i = 0; i < newOrder.allocations.length; i++) {
      const alloc = newOrder.allocations[i];
      if (!alloc.warehouse.trim()) {
        showToast('error', 'Validation Error', `Allocation ${i + 1}: Please select a warehouse`);
        return;
      }
      const allocQty = typeof alloc.quantity === 'string' ? parseInt(alloc.quantity) : alloc.quantity;
      if (allocQty === undefined || allocQty === null || isNaN(allocQty) || allocQty < 0) {
        showToast('error', 'Validation Error', `Allocation ${i + 1}: Please enter a valid quantity of 0 or greater`);
        return;
      }
    }

    // Convert allocations to API format
    const processedAllocations = newOrder.allocations.map(alloc => ({
      allocation_id: alloc.allocation_id,
      warehouse: alloc.warehouse,
      recommended_quantity: alloc.recommended_quantity ?? 0,
      final_quantity: (typeof alloc.quantity === 'string' ? parseInt(alloc.quantity) || 0 : alloc.quantity || 0) + (alloc.shipped_quantity ?? 0),
      is_locked: alloc.is_locked ?? false
    }));

    try {
      setSavingOrder(true);
      const wasEditing = Boolean(editingOrderId);
      if (editingOrderId) {
        const pricePerUnit = quantity > 0 ? totalCost / quantity : undefined;
        const updatePayload = {
          sku,
          product_name: productName,
          quantity,
          price_per_unit: pricePerUnit,
          total_cost: totalCost,
          manufacturer: newOrder.manufacturer || 'CN1',
          allocations: processedAllocations
            .filter((alloc) => typeof alloc.allocation_id === 'number')
            .map((alloc) => ({
              allocation_id: alloc.allocation_id as number,
              warehouse: alloc.warehouse,
              recommended_quantity: alloc.recommended_quantity,
              final_quantity: alloc.final_quantity,
              is_locked: alloc.is_locked
            }))
        };

        await (supplyChainService as any).updateOrderFull(editingOrderId, updatePayload);
        showToast('success', 'Order Updated', `Order updated successfully for ${productName}`);
      } else {
        // Let backend generate order number for uniqueness
        const result = await createOrderMutation.mutateAsync({
          manufacturer: newOrder.manufacturer || 'CN1',
          product_name: productName,
          sku: sku,
          quantity: quantity,
          total_cost: totalCost,
          allocations: processedAllocations.map((alloc) => ({
            warehouse: alloc.warehouse,
            recommended_quantity: alloc.recommended_quantity,
            final_quantity: alloc.final_quantity,
            is_locked: alloc.is_locked
          }))
        });

        const generatedOrderNumber = result?.order_number || 'New Order';
        showToast('success', 'Order Created', `Order ${generatedOrderNumber} created successfully for ${productName}`);
      }

      setShowAddOrderForm(false);
      resetNewOrderForm();
      if (wasEditing) {
        await refreshOrderManagementQueries();
      }
      if (hasFetchedBigQuery.current) {
        void fetchBigQueryData(false);
      }
    } catch (error) {
      console.error('Error saving order:', error);
      showToast('error', editingOrderId ? 'Failed to Update Order' : 'Failed to Create Order', String(error));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleEditOrder = async (orderId: number) => {
    try {
      setLoadingOrderDetails(true);
      const data = await (supplyChainService as any).getOrderDetailForEdit(orderId);
      const unitCost = data?.price_per_unit ?? getUnitCost(data?.product_name || '');
      const quantity = data?.quantity ?? 0;
      const totalCost = data?.total_cost ?? quantity * unitCost;

      setNewOrder({
        product_name: data?.product_name || '',
        sku: data?.sku || '',
        manufacturer: data?.manufacturer || 'CN1',
        quantity: quantity ? String(quantity) : '',
        date_placed: data?.date_ordered ? String(data.date_ordered).split('T')[0] : new Date().toISOString().split('T')[0],
        total_cost: totalCost ? Number(totalCost).toFixed(2) : '',
        estimated_arrival: '',
        allocations: (data?.allocations || []).map((alloc: any) => ({
          allocation_id: alloc.allocation_id,
          warehouse: alloc.warehouse || '',
          quantity: alloc.remaining_quantity ?? alloc.final_quantity ?? 0,
          recommended_quantity: alloc.recommended_quantity ?? 0,
          is_locked: alloc.is_locked ?? false,
          shipped_quantity: alloc.shipped_quantity ?? 0,
          remaining_quantity: alloc.remaining_quantity ?? alloc.final_quantity ?? 0
        }))
      });

      setEditingOrderId(orderId);
      setShowAddOrderForm(true);
      setActiveTab('in-progress');
    } catch (error) {
      console.error('Error fetching order details:', error);
      showToast('error', 'Failed to Load Order', String(error));
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const [deletingOrderId, setDeletingOrderId] = useState<number | null>(null);

  // Delete Order - uses React Query mutation for immediate cache invalidation
  const handleDeleteOrder = async (orderId: number) => {
    const confirmed = await confirm({
      title: 'Delete Order',
      message: 'Are you sure you want to delete this order? This will also delete all associated shipments.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (confirmed) {
      setDeletingOrderId(orderId);
      try {
        // Use mutation - automatically invalidates cache and refreshes UI
        await deleteOrderMutation.mutateAsync(orderId);
        // Cache is automatically invalidated - UI will refresh immediately
      } catch (error) {
        console.error('Error deleting order:', error);
        showToast('error', 'Delete Failed', `Failed to delete order: ${error}`);
      } finally {
        setDeletingOrderId(null);
      }
    }
  };

  const getAllocationShippedQuantity = (orderId: number, allocation: OrderAllocation): number => {
    if (typeof allocation.shipped_quantity === 'number') {
      return allocation.shipped_quantity;
    }

    return shipments.reduce((sum, shipment) => {
      if (shipment.order_id !== orderId) return sum;
      if (normalizeWarehouseName(shipment.destination) !== normalizeWarehouseName(allocation.warehouse)) return sum;
      return sum + (shipment.quantity || 0);
    }, 0);
  };

  const getAllocationRemainingQuantity = (orderId: number, allocation: OrderAllocation): number => {
    if (typeof allocation.remaining_quantity === 'number') {
      return allocation.remaining_quantity;
    }

    return Math.max(allocation.final_quantity - getAllocationShippedQuantity(orderId, allocation), 0);
  };

  const getShipmentableAllocations = (order: OrderInProgress): OrderAllocation[] => {
    return order.allocations.filter((allocation) => getAllocationRemainingQuantity(order.order_id, allocation) > 0);
  };

  const selectedShipmentOrder = selectedShipmentOrderId
    ? ordersInProgress.find((order) => order.order_id === selectedShipmentOrderId) || null
    : null;

  const selectedShipmentAllocation = selectedShipmentOrder
    ? selectedShipmentOrder.allocations.find(
      (allocation) => normalizeWarehouseName(allocation.warehouse) === normalizeWarehouseName(newShipment.destination)
    ) || null
    : null;

  const selectedShipmentRemaining = selectedShipmentOrder && selectedShipmentAllocation
    ? getAllocationRemainingQuantity(selectedShipmentOrder.order_id, selectedShipmentAllocation)
    : 0;

  const resetNewShipmentForm = () => {
    setShowAddShipmentForm(false);
    setSelectedShipmentOrderId(null);
    setNewShipment({
      destination: '',
      quantity: '',
      estimated_ship_date: '',
      estimated_arrival_date: ''
    });
  };

  const handleOpenShipmentForm = (orderId: number) => {
    const order = ordersInProgress.find((currentOrder) => currentOrder.order_id === orderId);
    if (!order) {
      showToast('error', 'Order Not Found', 'Unable to load the selected order');
      return;
    }

    const availableAllocations = getShipmentableAllocations(order);
    if (availableAllocations.length === 0) {
      showToast('info', 'No Remaining Allocation', 'All allocated stock has already been assigned to shipments');
      return;
    }

    setSelectedShipmentOrderId(orderId);
    setNewShipment({
      destination: availableAllocations[0].warehouse,
      quantity: '',
      estimated_ship_date: '',
      estimated_arrival_date: ''
    });
    setShowAddShipmentForm(true);
  };

  const handleCreatePartialShipment = async () => {
    if (!selectedShipmentOrder) return;

    const quantity = typeof newShipment.quantity === 'string' ? parseInt(newShipment.quantity) : newShipment.quantity;
    if (!newShipment.destination) {
      showToast('error', 'Validation Error', 'Please select a warehouse');
      return;
    }
    if (!quantity || quantity <= 0 || isNaN(quantity)) {
      showToast('error', 'Validation Error', 'Shipment quantity must be greater than 0');
      return;
    }
    if (quantity > selectedShipmentRemaining) {
      showToast('error', 'Validation Error', `Shipment quantity exceeds remaining allocation (${selectedShipmentRemaining.toLocaleString()})`);
      return;
    }

    try {
      setSavingManualShipment(true);
      await supplyChainService.createShipment({
        order_id: selectedShipmentOrder.order_id,
        order_number: selectedShipmentOrder.order_number,
        sku: selectedShipmentOrder.sku,
        quantity,
        destination: newShipment.destination,
        estimated_ship_date: newShipment.estimated_ship_date || undefined,
        estimated_arrival_date: newShipment.estimated_arrival_date || undefined
      });

      resetNewShipmentForm();
      await refreshOrderManagementQueries();
      if (hasFetchedBigQuery.current) {
        void fetchBigQueryData(false);
      }
      setActiveTab('shipments');
      showToast('success', 'Shipment Created', `Created shipment of ${quantity.toLocaleString()} units to ${newShipment.destination}`);
    } catch (error) {
      console.error('Error creating shipment:', error);
      showToast('error', 'Failed to Create Shipment', String(error));
    } finally {
      setSavingManualShipment(false);
    }
  };

  //  Delete All Test Orders - uses React Query mutation for immediate cache invalidation
  const handleDeleteAllTestOrders = async () => {
    const testOrders = ordersInProgress.filter(o => o.product_name === 'nhh product');
    if (testOrders.length === 0) return;

    const confirmed = await confirm({
      title: 'Delete Test Orders',
      message: `Delete ${testOrders.length} test orders? This cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete All',
      cancelText: 'Cancel'
    });

    if (confirmed) {
      setDeletingOrderId(-1); // Use -1 to indicate "deleting all"

      try {
        // Delete each order using mutation
        for (const order of testOrders) {
          await deleteOrderMutation.mutateAsync(order.order_id);
        }
        // Cache is automatically invalidated after each deletion
      } catch (error) {
        console.error('Error deleting test orders:', error);
        showToast('error', 'Delete Failed', `Failed to delete some orders: ${error}`);
      } finally {
        setDeletingOrderId(null);
      }
    }
  };

  const handlePlaceOrder = (order: OrderToBePlaced) => {
    // Pre-fill the form with order data
    const quantity = order.net_quantity_needed || order.quantity_needed;
    const productSku = PRODUCT_SKU_MAPPING[order.product_name] || '';
    setNewOrder({
      product_name: order.product_name,
      sku: productSku,
      manufacturer: 'CN1', // Default to China Manufacturer 1
      quantity: quantity.toString(),
      date_placed: new Date().toISOString().split('T')[0],
      total_cost: (quantity * getUnitCost(order.product_name)).toFixed(2),
      estimated_arrival: order.order_cutoff_date,
      allocations: [{
        warehouse: order.country,
        quantity: quantity,
        estimated_arrival: order.order_cutoff_date
      }]
    });
    setEditingOrderId(null);
    setShowAddOrderForm(true);
    setActiveTab('in-progress');
  };

  const addAllocationToNewOrder = () => {
    setNewOrder({
      ...newOrder,
      allocations: [...newOrder.allocations, { warehouse: '', quantity: '' }]
    });
  };

  const updateAllocation = (index: number, field: keyof FormAllocation, value: any) => {
    const updated = [...newOrder.allocations];
    updated[index] = { ...updated[index], [field]: value };
    setNewOrder({ ...newOrder, allocations: updated });
  };

  const removeAllocation = (index: number) => {
    const updated = newOrder.allocations.filter((_, i) => i !== index);
    setNewOrder({ ...newOrder, allocations: updated });
  };

  const resetNewOrderForm = () => {
    setEditingOrderId(null);
    setNewOrder({
      product_name: '',
      sku: '',
      manufacturer: 'CN1',
      quantity: '',
      date_placed: new Date().toISOString().split('T')[0],
      total_cost: '',
      estimated_arrival: '',
      allocations: []
    });
  };

  // ============================================================================
  // SHIPMENT MANAGEMENT FUNCTIONS
  // ============================================================================

  const normalizeTrackingEntries = (entries?: TrackingEntry[] | null): TrackingEntry[] => {
    if (!entries || entries.length === 0) return [];

    const normalized: TrackingEntry[] = [];
    const seen = new Set<string>();
    for (const entry of entries) {
      const trackingNumber = (entry?.tracking_number || '').trim();
      if (!trackingNumber || seen.has(trackingNumber)) continue;
      seen.add(trackingNumber);
      normalized.push({
        tracking_number: trackingNumber,
        tracking_url: (entry?.tracking_url || '').trim() || null,
        cartons_count: entry?.cartons_count ?? null
      });
    }
    return normalized;
  };

  const getShipmentTrackingEntries = (shipment: Shipment): TrackingEntry[] => {
    const normalizedExisting = normalizeTrackingEntries(shipment.tracking_entries);
    if (normalizedExisting.length > 0) return normalizedExisting;
    if (!shipment.tracking_number) return [];
    return [{
      tracking_number: shipment.tracking_number,
      tracking_url: shipment.tracking_url || null,
      cartons_count: null
    }];
  };

  const getEditableTrackingEntries = (shipment: Shipment): TrackingEntry[] => {
    const existingEntries = getShipmentTrackingEntries(shipment);
    if (existingEntries.length > 0) return existingEntries;
    return [{
      tracking_number: '',
      tracking_url: null,
      cartons_count: null
    }];
  };

  const statusRequiresTracking = (status: string): boolean => {
    return ['in_transit', 'customs_clearance', 'arrived_at_warehouse', 'complete'].includes(status);
  };

  const isShipmentOverdue = (shipment: Shipment): boolean => {
    if (!shipment.estimated_arrival_date) return false;
    if (shipment.status === 'arrived_at_warehouse' || shipment.status === 'complete') return false;
    const etaDate = new Date(shipment.estimated_arrival_date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    etaDate.setHours(0, 0, 0, 0);
    return etaDate.getTime() < now.getTime();
  };

  const isTrackingMissingForShipment = (shipment: Shipment): boolean => {
    if (!statusRequiresTracking(shipment.status)) return false;
    return getShipmentTrackingEntries(shipment).length === 0;
  };

  const copyToClipboard = async (value: string, label: string) => {
    if (!value?.trim()) return;
    try {
      await navigator.clipboard.writeText(value.trim());
      showToast('success', 'Copied', `${label} copied`);
    } catch {
      showToast('error', 'Copy Failed', `Unable to copy ${label.toLowerCase()}`);
    }
  };

  const validateTrackingEntriesForSave = (shipment: Shipment): string[] => {
    const validationErrors: string[] = [];
    const seenTrackingNumbers = new Set<string>();
    const normalizedEntries = normalizeTrackingEntries(shipment.tracking_entries);

    normalizedEntries.forEach((entry, index) => {
      const trackingNumber = (entry.tracking_number || '').trim();
      const trackingUrl = (entry.tracking_url || '').trim();
      const cartonsCount = entry.cartons_count;

      if (!trackingNumber) {
        validationErrors.push(`Tracking row ${index + 1}: tracking number is required`);
      } else if (seenTrackingNumbers.has(trackingNumber.toLowerCase())) {
        validationErrors.push(`Tracking row ${index + 1}: duplicate tracking number`);
      } else {
        seenTrackingNumbers.add(trackingNumber.toLowerCase());
      }

      if (trackingUrl) {
        try {
          const parsedUrl = new URL(trackingUrl);
          if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            validationErrors.push(`Tracking row ${index + 1}: URL must start with http or https`);
          }
        } catch {
          validationErrors.push(`Tracking row ${index + 1}: invalid tracking URL`);
        }
      }

      if (cartonsCount !== null && cartonsCount !== undefined) {
        if (!Number.isInteger(cartonsCount)) {
          validationErrors.push(`Tracking row ${index + 1}: cartons must be a whole number`);
        } else if (cartonsCount < 0) {
          validationErrors.push(`Tracking row ${index + 1}: cartons cannot be negative`);
        }
      }
    });

    if (statusRequiresTracking(shipment.status) && normalizedEntries.length === 0) {
      validationErrors.push('At least one tracking entry is required for this status');
    }

    return validationErrors;
  };

  const handleEditShipment = (shipment: Shipment) => {
    setShipmentFormErrors([]);
    setStatusDropdownOpen(false);
    setShippingMethodDropdownOpen(false);
    setEditingShipment({
      ...shipment,
      shipping_method: shipment.shipping_method || 'Air Freight',
      tracking_entries: getEditableTrackingEntries(shipment)
    });
  };

  const updateTrackingEntry = (
    index: number,
    field: keyof TrackingEntry,
    value: string | number | null
  ) => {
    if (!editingShipment) return;
    const currentEntries = editingShipment.tracking_entries || [];
    const updatedEntries = [...currentEntries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      [field]: value
    } as TrackingEntry;
    setEditingShipment({
      ...editingShipment,
      tracking_entries: updatedEntries
    });
  };

  const addTrackingEntry = () => {
    if (!editingShipment) return;
    const currentEntries = editingShipment.tracking_entries || [];
    setEditingShipment({
      ...editingShipment,
      tracking_entries: [
        ...currentEntries,
        { tracking_number: '', tracking_url: null, cartons_count: null }
      ]
    });
  };

  const removeTrackingEntry = (index: number) => {
    if (!editingShipment) return;
    const currentEntries = editingShipment.tracking_entries || [];
    const updatedEntries = currentEntries.filter((_, entryIndex) => entryIndex !== index);
    setEditingShipment({
      ...editingShipment,
      tracking_entries: updatedEntries.length > 0 ? updatedEntries : [{ tracking_number: '', tracking_url: null, cartons_count: null }]
    });
  };

  //  uses React Query mutation for immediate cache invalidation
  const handleSaveShipment = async () => {
    if (!editingShipment) return;

    setSavingShipment(true);
    try {
      const trackingValidationErrors = validateTrackingEntriesForSave(editingShipment);
      setShipmentFormErrors(trackingValidationErrors);
      if (trackingValidationErrors.length > 0) {
        throw new Error(trackingValidationErrors[0]);
      }

      // Validate field lengths before sending
      const validateLength = (field: string | null | undefined, maxLength: number, fieldName: string): string | undefined => {
        if (!field) return undefined;
        const trimmed = field.trim();
        if (trimmed === '') return undefined;
        if (trimmed.length > maxLength) {
          throw new Error(`${fieldName} exceeds maximum length of ${maxLength} characters`);
        }
        return trimmed;
      };

      // Clean up empty strings to undefined/null to prevent backend parsing errors
      const Date = (date: string | null) => {
        if (!date || date.trim() === '') return undefined;
        return date;
      };

      await updateShipmentMutation.mutateAsync({
        shipmentId: editingShipment.shipment_id,
        data: {
          status: editingShipment.status,
          estimated_ship_date: Date(editingShipment.estimated_ship_date),
          estimated_arrival_date: Date(editingShipment.estimated_arrival_date),
          actual_ship_date: Date(editingShipment.actual_ship_date),
          actual_arrival_date: Date(editingShipment.actual_arrival_date),
          supplier: validateLength(editingShipment.supplier, 100, 'Supplier'),
          shipping_method: validateLength(editingShipment.shipping_method, 50, 'Shipping Method'),
          tracking_entries: normalizeTrackingEntries(editingShipment.tracking_entries).map((entry) => {
            const trackingNumber = validateLength(entry.tracking_number, 100, 'Tracking Number');
            const trackingUrl = validateLength(entry.tracking_url, 500, 'Tracking URL');
            if (!trackingNumber) {
              throw new Error('Tracking Number is required for each tracking row');
            }

            const cartonsValue = entry.cartons_count;
            if (cartonsValue !== null && cartonsValue !== undefined) {
              if (!Number.isInteger(cartonsValue)) {
                throw new Error('Cartons Count must be a whole number');
              }
              if (cartonsValue < 0) {
                throw new Error('Cartons Count cannot be negative');
              }
            }

            return {
              tracking_number: trackingNumber,
              tracking_url: trackingUrl || null,
              cartons_count: cartonsValue ?? null
            };
          }),
          shipping_reference: validateLength(editingShipment.shipping_reference, 100, 'Reference / BOL'),
          notes: editingShipment.notes?.trim() || undefined
        }
      });

      // Cache is automatically invalidated - UI will refresh immediately
      setShipmentFormErrors([]);
      setStatusDropdownOpen(false);
      setShippingMethodDropdownOpen(false);
      setEditingShipment(null);
      showToast('success', 'Shipment Updated', 'Shipment details saved successfully');
    } catch (error: any) {
      console.error('Error updating shipment:', error);
      const errorMessage = error?.message || error?.toString() || 'Failed to update shipment. Please try again.';
      showToast('error', 'Update Failed', errorMessage);
    } finally {
      setSavingShipment(false);
    }
  };

  // Delete Shipment - uses React Query mutation for immediate cache invalidation
  const handleDeleteShipment = async (shipment: Shipment) => {
    const confirmed = await confirm({
      title: 'Delete Shipment',
      message: `Delete shipment for ${shipment.sku} to ${shipment.destination}? This cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });

    if (confirmed) {
      try {
        await deleteShipmentMutation.mutateAsync(shipment.shipment_id);
        showToast('success', 'Shipment Deleted', `Shipment for ${shipment.sku} deleted successfully`);
      } catch (error) {
        console.error('Error deleting shipment:', error);
        showToast('error', 'Delete Failed', String(error));
      }
    }
  };

  // Fetch change history for a shipment
  const handleViewShipmentLog = async (shipment: Shipment) => {
    setSelectedShipmentForLog(shipment);
    setShowLogModal(true);
    setLoadingLogs(true);

    try {
      const response = await supplyChainService.getShipmentLogs(shipment.shipment_id);
      setShipmentLogs(response.logs || []);
    } catch (error) {
      console.error('Error fetching shipment logs:', error);
      setShipmentLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Format field names for display
  const formatFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      'status': 'Status',
      'actual_ship_date': 'Actual Ship Date',
      'actual_arrival_date': 'Actual Arrival Date',
      'estimated_ship_date': 'Estimated Ship Date',
      'estimated_arrival_date': 'Estimated Arrival Date',
      'supplier': 'Supplier',
      'shipping_method': 'Shipping Method',
      'tracking_number': 'Tracking Number',
      'tracking_url': 'Tracking URL',
      'tracking_entries': 'Tracking Entries',
      'shipping_reference': 'Reference #',
      'notes': 'Notes',
    };
    return fieldNames[field] || field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Format action types for display
  const formatAction = (action: string): { label: string; color: string; icon: string } => {
    const actions: Record<string, { label: string; color: string; icon: string }> = {
      'created': { label: 'Created', color: 'text-green-600 bg-green-50', icon: '🆕' },
      'status_changed': { label: 'Status Changed', color: 'text-blue-600 bg-blue-50', icon: '🔄' },
      'dates_updated': { label: 'Dates Updated', color: 'text-purple-600 bg-purple-50', icon: '📅' },
      'details_updated': { label: 'Details Updated', color: 'text-orange-600 bg-orange-50', icon: '✏️' },
      'updated': { label: 'Updated', color: 'text-gray-600 bg-gray-50', icon: '📝' },
    };
    return actions[action] || { label: action, color: 'text-gray-600 bg-gray-50', icon: '📝' };
  };

  const getStatusSteps = () => [
    { key: 'pending_details', label: 'Pending Details', icon: '📝' },
    { key: 'pending_ship', label: 'Ready to Ship', icon: '📦' },
    { key: 'in_transit', label: 'In Transit', icon: '🚢' },
    { key: 'customs_clearance', label: 'Customs Clearance', icon: '🛃' },
    { key: 'arrived_at_warehouse', label: 'Arrived', icon: '🏭' },
    { key: 'complete', label: 'Complete', icon: '✅' }
  ];

  const getStatusIndex = (status: string) => {
    const steps = getStatusSteps();
    return steps.findIndex(s => s.key === status);
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTotalAllocated = () => {
    return newOrder.allocations.reduce((sum, alloc) => {
      const qty = typeof alloc.quantity === 'string' ? parseInt(alloc.quantity) || 0 : alloc.quantity || 0;
      return sum + qty;
    }, 0);
  };

  const getTotalShippedInForm = () => {
    return newOrder.allocations.reduce((sum, alloc) => sum + (alloc.shipped_quantity ?? 0), 0);
  };

  const getOrderQuantity = () => {
    return typeof newOrder.quantity === 'string' ? (parseInt(newOrder.quantity) || 0) : (newOrder.quantity || 0);
  };

  const getAllocationTargetQuantity = () => {
    return editingOrderId ? Math.max(getOrderQuantity() - getTotalShippedInForm(), 0) : getOrderQuantity();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="w-full">
      {/* Header handled by Dashboard */}

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('orders-to-place')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'orders-to-place'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          📋 Orders to Place
        </button>
        <button
          onClick={() => setActiveTab('in-progress')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'in-progress'
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          🚢 Orders In Progress ({ordersInProgress.length})
        </button>
        <button
          onClick={() => setActiveTab('shipments')}
          className={`px-6 py-3 font-medium transition-colors ${activeTab === 'shipments'
            ? 'border-b-2 border-green-600 text-green-600'
            : 'text-gray-600 hover:text-gray-900'
            }`}
        >
          📦 Track Shipments ({shipments.length})
        </button>
      </div>

      {/* Summary Cards */}
      {
        summary && activeTab === 'orders-to-place' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600">Products to Order</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{groupedOrders.length}</div>
                <div className="text-xs text-gray-400">{ordersToPlace.length} warehouse orders</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-gray-600">Total Required</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {ordersToPlace.reduce((sum, o) => sum + o.quantity_needed, 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-400">before in-progress</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-blue-600">In Progress</div>
                <div className="text-2xl font-bold text-blue-600 mt-1">
                  -{ordersToPlace.reduce((sum, o) => sum + (o.quantity_in_progress || 0), 0).toLocaleString()}
                </div>
                <div className="text-xs text-blue-400">already ordered</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-purple-600">In Transit</div>
                <div className="text-2xl font-bold text-purple-600 mt-1">
                  -{ordersToPlace.reduce((sum, o) => sum + (o.quantity_in_transit || 0), 0).toLocaleString()}
                </div>
                <div className="text-xs text-purple-400">shipments on the way</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-green-600">Net Still Needed</div>
                <div className="text-2xl font-bold text-green-600 mt-1">
                  {ordersToPlace.reduce((sum, o) => sum + (o.net_quantity_needed ?? o.quantity_needed), 0).toLocaleString()}
                </div>
                <div className="text-xs text-green-400">to place now</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-sm text-orange-600">Est. Net Cost</div>
                <div className="text-2xl font-bold text-orange-600 mt-1">
                  ${groupedOrders.reduce((sum, g) => sum + g.total_estimated_cost, 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </div>
                <div className="text-xs text-orange-400">based on net needed</div>
              </div>
            </div>

            {/* Per-Destination Breakdown Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow p-6 mb-6 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    {/* <span className="text-2xl">🌍</span> */}
                    Per-Destination Breakdown
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Required vs In-Progress & In-Transit by destination warehouse</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {(() => {
                  // Country CODES — order.country uses these, not full names
                  const destinations = ['US', 'CA', 'UK', 'AU'];
                  return destinations.map(dest => {
                    const destOrders = ordersToPlace.filter(o => o.country === dest);
                    const totalRequired = destOrders.reduce((sum, o) => sum + o.quantity_needed, 0);
                    const inProgress = destOrders.reduce((sum, o) => sum + (o.quantity_in_progress || 0), 0);
                    const inTransit = destOrders.reduce((sum, o) => sum + (o.quantity_in_transit || 0), 0);
                    const netNeeded = destOrders.reduce((sum, o) => sum + (o.net_quantity_needed ?? o.quantity_needed), 0);

                    if (totalRequired === 0 && inProgress === 0) return null;

                    return (
                      <div key={dest} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <div className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <span className="text-lg">{dest === 'US' ? '🇺🇸' : dest === 'CA' ? '🇨🇦' : dest === 'UK' ? '🇬🇧' : '🇦🇺'}</span>
                          {dest}
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">Required:</span>
                            <span className="text-sm font-medium text-gray-900">{totalRequired.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-blue-600">In Progress:</span>
                            <span className="text-sm font-semibold text-blue-600">-{inProgress.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-purple-600">In Transit:</span>
                            <span className="text-sm font-semibold text-purple-600">-{inTransit.toLocaleString()}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                            <span className="text-xs font-semibold text-green-700">Net Needed:</span>
                            <span className="text-lg font-bold text-green-700">{netNeeded.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </>
        )
      }

      {/* Loading States - Skeleton UI */}
      {
        (loadingBigQuery || loadingOrders) && activeTab === 'orders-to-place' && (
          <div className="space-y-6">
            {/* Summary Cards Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow p-4">
                  <div className="h-3 bg-gray-200 rounded w-20 animate-pulse mb-2"></div>
                  <div className="h-7 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* Table Skeleton */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {/* Table Header */}
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className="grid grid-cols-7 gap-4">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-gray-200">
                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className="px-6 py-4">
                    <div className="grid grid-cols-7 gap-4 items-center">
                      {/* Expand Icon */}
                      <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      {/* Product Column */}
                      <div className="space-y-2">
                        <div className="h-5 bg-gray-200 rounded w-28 animate-pulse"></div>
                        <div className="h-3 bg-gray-100 rounded w-20 animate-pulse"></div>
                      </div>
                      {/* Priority */}
                      <div className="h-6 bg-gray-200 rounded w-16 animate-pulse"></div>
                      {/* Quantity */}
                      <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
                      {/* Cutoff */}
                      <div className="space-y-1">
                        <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
                        <div className="h-3 bg-gray-100 rounded w-14 animate-pulse"></div>
                      </div>
                      {/* Warehouses */}
                      <div className="flex gap-2 justify-center">
                        <div className="h-5 bg-gray-200 rounded w-8 animate-pulse"></div>
                        <div className="h-5 bg-gray-200 rounded w-8 animate-pulse"></div>
                      </div>
                      {/* Cost */}
                      <div className="h-5 bg-gray-200 rounded w-16 animate-pulse"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Loading Indicator */}
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                <span className="ml-3 text-sm">
                  {loadingOrders ? 'Loading orders and shipments data...' : 'Loading inventory data...'}
                </span>
              </div>
            </div>
          </div>
        )
      }

      {
        loadingOrders && (activeTab === 'in-progress' || activeTab === 'shipments') && (
          <div className="space-y-6">
            {/* Table Skeleton */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {/* Table Header */}
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className="grid grid-cols-8 gap-4">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-gray-200">
                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className="px-6 py-4">
                    <div className="grid grid-cols-8 gap-4 items-center">
                      {/* Order Number */}
                      <div className="h-5 bg-gray-200 rounded w-24 animate-pulse"></div>
                      {/* Product */}
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                        <div className="h-3 bg-gray-100 rounded w-16 animate-pulse"></div>
                      </div>
                      {/* Other Columns */}
                      {[...Array(6)].map((_, colIndex) => (
                        <div key={colIndex} className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Loading Indicator */}
            <div className="flex items-center justify-center py-4">
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                <span className="ml-3 text-sm">Loading orders from database...</span>
              </div>
            </div>
          </div>
        )
      }

      {
        error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        )
      }

      {/* ORDERS TO PLACE TAB CONTENT - Grouped by Product */}
      {/* Only show when both BigQuery AND React Query have finished loading */}
      {
        !loadingBigQuery && !loadingOrders && !error && activeTab === 'orders-to-place' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Needed
                      <div className="text-xs normal-case text-gray-400 font-normal">(after in-progress)</div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Earliest Cutoff
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Warehouses
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unit Cost
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Est. Total Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groupedOrders.map((group) => (
                    <React.Fragment key={group.product_name}>
                      {/* Product Summary Row */}
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleProductExpand(group.product_name)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <svg
                            className={`w-5 h-5 text-gray-400 transition-transform ${expandedProducts.has(group.product_name) ? 'rotate-90' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-gray-900">{group.product_name}</div>
                          <div className="text-xs text-gray-500">
                            {group.orders_within_30_days} warehouse{group.orders_within_30_days !== 1 ? 's' : ''} need reorder
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(group.priority)}`}>
                            {group.priority?.toUpperCase() || 'NORMAL'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <div className="font-bold text-gray-900">{group.total_quantity_needed.toLocaleString()}</div>
                          <div className="text-xs text-gray-500">units</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div>{formatDate(group.earliest_cutoff_date)}</div>
                          <div className={`text-xs ${group.earliest_days_until_cutoff <= 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                            {group.earliest_days_until_cutoff <= 0 ? 'OVERDUE' : `in ${group.earliest_days_until_cutoff} days`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {group.country_breakdown.map(o => (
                              <span key={o.country} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-600">
                                {o.country}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-600">
                          ${group.unit_cost.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                          <div className="font-bold text-gray-900">${group.total_estimated_cost.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                        </td>
                      </tr>

                      {/* Expanded Country Breakdown */}
                      {expandedProducts.has(group.product_name) && group.country_breakdown.map((order, idx) => (
                        <tr key={`${group.product_name}-${order.country}`} className="bg-blue-50/50">
                          <td className="px-6 py-3"></td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2 pl-4">
                              <span className="text-gray-400">└</span>
                              <span className="font-medium text-gray-700">{order.country}</span>
                            </div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(order.priority)}`}>
                              {order.priority?.toUpperCase() || 'NORMAL'}
                            </span>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-right">
                            <div className="font-semibold text-green-700">{(order.net_quantity_needed ?? order.quantity_needed).toLocaleString()}</div>
                            {(order.quantity_in_progress || order.quantity_in_transit) ? (
                              <div className="text-xs space-y-0.5 mt-1">
                                <div className="text-gray-500">Required: {order.quantity_needed.toLocaleString()}</div>
                                {order.quantity_in_progress ? (
                                  <div className="text-blue-600">Progress: -{order.quantity_in_progress.toLocaleString()}</div>
                                ) : null}
                                {order.quantity_in_transit ? (
                                  <div className="text-purple-600">In transit: -{order.quantity_in_transit.toLocaleString()}</div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400 mt-1">No orders in progress</div>
                            )}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                            <div>{formatDate(order.order_cutoff_date)}</div>
                            <div className="text-xs text-gray-400">in {order.days_until_cutoff} days</div>
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-center text-gray-600">
                            Stock: {order.current_stock.toLocaleString()}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                            —
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-right">
                            <div className="text-gray-700">${((order.net_quantity_needed ?? order.quantity_needed) * group.unit_cost).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                            {(order.net_quantity_needed ?? order.quantity_needed) > 0 && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlaceOrder(order); }}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-1"
                              >
                                Place Order →
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      {/* IN-PROGRESS ORDERS TAB CONTENT - loads from Supabase */}
      {
        !loadingOrders && activeTab === 'in-progress' && (
          <div>
            {/* Add Order Button */}
            <div className="mb-4">
              {!showAddOrderForm && (
                <button
                  onClick={() => setShowAddOrderForm(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium transition-colors"
                >
                  + Add Order Manually
                </button>
              )}
            </div>

            {/* Add Order Form */}
            {showAddOrderForm && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{editingOrderId ? 'Update Order In Progress' : 'Add Order In Progress'}</h3>
                  <button
                    onClick={() => {
                      setShowAddOrderForm(false);
                      resetNewOrderForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                    <LightDropdown
                      value={newOrder.product_name}
                      onChange={(value) => {
                        const selectedProduct = AVAILABLE_PRODUCTS.find(p => p.name === value);
                        setNewOrder({
                          ...newOrder,
                          product_name: value,
                          sku: selectedProduct?.sku || ''
                        });
                      }}
                      placeholder="Select a product..."
                      options={AVAILABLE_PRODUCTS.map((product) => ({
                        value: product.name,
                        label: `${product.name} - ${product.description}`
                      }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <input
                      type="text"
                      value={newOrder.sku}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600"
                      placeholder="Auto-filled from product"
                    />
                    <p className="text-xs text-gray-400 mt-1">Auto-filled based on product selection</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Quantity *</label>
                    <input
                      type="number"
                      value={newOrder.quantity}
                      onChange={(e) => {
                        const qty = parseInt(e.target.value) || 0;
                        const unitCost = getUnitCost(newOrder.product_name);
                        setNewOrder({
                          ...newOrder,
                          quantity: e.target.value,
                          total_cost: (qty * unitCost).toFixed(2)
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="1000"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Placed *</label>
                    <input
                      type="date"
                      value={newOrder.date_placed}
                      onChange={(e) => setNewOrder({ ...newOrder, date_placed: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer *</label>
                    <LightDropdown
                      value={newOrder.manufacturer || 'CN1'}
                      onChange={(value) => setNewOrder({ ...newOrder, manufacturer: value })}
                      options={MANUFACTURERS.map((mfr) => ({
                        value: mfr.code,
                        label: `${mfr.code} - ${mfr.name}`
                      }))}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Order #: {generateOrderNumberPreview(newOrder.manufacturer || 'CN1', newOrder.date_placed)} (auto-assigned)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">$</span>
                      <input
                        type="text"
                        value={newOrder.total_cost}
                        readOnly
                        className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600"
                        placeholder="Auto-calculated"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {newOrder.product_name ? `${newOrder.quantity || 0} × $${getUnitCost(newOrder.product_name).toFixed(2)}/unit` : 'Select product first'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Arrival</label>
                    <input
                      type="date"
                      min={new Date().toISOString().split('T')[0]}
                      value={newOrder.estimated_arrival}
                      onChange={(e) => setNewOrder({ ...newOrder, estimated_arrival: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Allocations Section */}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Warehouse Allocations
                      {getAllocationTargetQuantity() > 0 && (
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          ({getAllocationTargetQuantity().toLocaleString()} units {editingOrderId ? 'remaining' : 'total'} - How should these be distributed?)
                        </span>
                      )}
                    </h4>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          // Quick allocate based on outstanding needs for this product
                          if (!newOrder.product_name || getAllocationTargetQuantity() <= 0) {
                            showToast('warning', 'Cannot Auto-Allocate', 'Please select a product and enter a quantity first');
                            return;
                          }

                          // Find all destinations that need this product
                          const productOrders = ordersToPlace.filter(o => o.product_name === newOrder.product_name && (o.net_quantity_needed ?? o.quantity_needed) > 0);

                          if (productOrders.length === 0) {
                            showToast('info', 'No Allocations Needed', 'No destinations currently need this product');
                            return;
                          }

                          // Calculate total needed across all destinations
                          const totalNeeded = productOrders.reduce((sum, o) => sum + (o.net_quantity_needed ?? o.quantity_needed), 0);
                          const orderQty = getAllocationTargetQuantity();

                          // Allocate proportionally based on need
                          let allocated = 0;
                          const autoAllocations = productOrders.map((o, idx) => {
                            const proportion = (o.net_quantity_needed ?? o.quantity_needed) / totalNeeded;
                            let qty = Math.round(orderQty * proportion);

                            // For the last allocation, adjust to ensure total matches exactly
                            if (idx === productOrders.length - 1) {
                              qty = orderQty - allocated;
                            } else {
                              allocated += qty;
                            }

                            return {
                              warehouse: o.country,
                              quantity: qty,
                              estimated_arrival: o.order_cutoff_date
                            };
                          }).filter(a => a.quantity > 0);

                          setNewOrder({ ...newOrder, allocations: autoAllocations });
                          showToast('success', 'Auto-Allocated', `${autoAllocations.length} allocations created based on outstanding needs`);
                        }}
                        className="text-sm text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                        title="Auto-allocate based on outstanding needs per destination"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Quick Allocate
                      </button>
                      <button
                        onClick={addAllocationToNewOrder}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        + Add Allocation
                      </button>
                    </div>
                  </div>

                  {newOrder.allocations.length === 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 font-medium mb-2">📦 No allocations yet</p>
                      <p className="text-xs text-blue-600 mb-3">
                        You need to specify how to distribute the {getAllocationTargetQuantity().toLocaleString()} units across warehouses.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            // Quick allocate based on outstanding needs for this product
                            if (!newOrder.product_name || getAllocationTargetQuantity() <= 0) {
                              showToast('warning', 'Cannot Auto-Allocate', 'Please select a product and enter a quantity first');
                              return;
                            }

                            const productOrders = ordersToPlace.filter(o => o.product_name === newOrder.product_name && (o.net_quantity_needed ?? o.quantity_needed) > 0);

                            if (productOrders.length === 0) {
                              showToast('info', 'No Allocations Needed', 'No destinations currently need this product');
                              return;
                            }

                            const totalNeeded = productOrders.reduce((sum, o) => sum + (o.net_quantity_needed ?? o.quantity_needed), 0);
                            const orderQty = getAllocationTargetQuantity();

                            let allocated = 0;
                            const autoAllocations = productOrders.map((o, idx) => {
                              const proportion = (o.net_quantity_needed ?? o.quantity_needed) / totalNeeded;
                              let qty = Math.round(orderQty * proportion);

                              if (idx === productOrders.length - 1) {
                                qty = orderQty - allocated;
                              } else {
                                allocated += qty;
                              }

                              return {
                                warehouse: o.country,
                                quantity: qty,
                                estimated_arrival: o.order_cutoff_date
                              };
                            }).filter(a => a.quantity > 0);

                            setNewOrder({ ...newOrder, allocations: autoAllocations });
                            showToast('success', 'Auto-Allocated', `${autoAllocations.length} allocations created based on outstanding needs`);
                          }}
                          className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 font-medium"
                        >
                          ⚡ Quick Allocate (Auto)
                        </button>
                        <button
                          onClick={addAllocationToNewOrder}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 font-medium"
                        >
                          + Manual Allocation
                        </button>
                      </div>
                    </div>
                  )}

                  {newOrder.allocations.map((alloc, index) => (
                    <div key={index} className="flex gap-3 mb-2 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Warehouse</label>
                        <LightDropdown
                          value={alloc.warehouse}
                          onChange={(value) => updateAllocation(index, 'warehouse', value)}
                          placeholder="Select..."
                          options={[
                            { value: '', label: 'Select...' },
                            { value: 'UK', label: 'UK' },
                            { value: 'US', label: 'US' },
                            { value: 'CA', label: 'Canada' },
                            { value: 'AU', label: 'Australia' },
                            { value: 'CN', label: 'China' }
                          ]}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={alloc.quantity}
                          onChange={(e) => updateAllocation(index, 'quantity', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="500"
                          min="1"
                        />
                        {(editingOrderId && ((alloc.shipped_quantity ?? 0) > 0 || typeof alloc.remaining_quantity === 'number')) && (
                          <p className="text-xs text-gray-500 mt-1">
                            Allocated Total: {((typeof alloc.quantity === 'string' ? (parseInt(alloc.quantity) || 0) : (alloc.quantity || 0)) + (alloc.shipped_quantity ?? 0)).toLocaleString()} | Shipped: {(alloc.shipped_quantity ?? 0).toLocaleString()} | Remaining: {(typeof alloc.quantity === 'string' ? (parseInt(alloc.quantity) || 0) : (alloc.quantity || 0)).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => removeAllocation(index)}
                        className="px-3 py-2 text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}

                  {newOrder.allocations.length > 0 && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-700">Total Allocated:</span>
                        <span className={`font-bold ${getTotalAllocated() === getAllocationTargetQuantity() ? 'text-green-600' : 'text-red-600'}`}>
                          {getTotalAllocated()} / {getAllocationTargetQuantity()}
                        </span>
                      </div>
                      {getTotalAllocated() !== getAllocationTargetQuantity() && (
                        <p className="text-xs text-red-600 mt-1">
                          {editingOrderId ? 'Remaining allocation must equal remaining order quantity' : 'Allocated quantity must equal total order quantity'}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Form Actions */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleAddOrder}
                    disabled={getTotalAllocated() !== getAllocationTargetQuantity() || getAllocationTargetQuantity() <= 0 || savingOrder}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingOrder ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {editingOrderId ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      editingOrderId ? 'Update Order' : 'Save Order'
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddOrderForm(false);
                      resetNewOrderForm();
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {showAddShipmentForm && selectedShipmentOrder && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Add Partial Shipment</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedShipmentOrder.order_number} · {selectedShipmentOrder.product_name}
                    </p>
                  </div>
                  <button
                    onClick={resetNewShipmentForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Warehouse Allocation *</label>
                    <LightDropdown
                      value={newShipment.destination}
                      onChange={(value) => setNewShipment({ ...newShipment, destination: value, quantity: '' })}
                      options={getShipmentableAllocations(selectedShipmentOrder).map((allocation) => ({
                        value: allocation.warehouse,
                        label: `${allocation.warehouse} - ${getAllocationRemainingQuantity(selectedShipmentOrder.order_id, allocation).toLocaleString()} remaining`
                      }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shipment Quantity *</label>
                    <input
                      type="number"
                      value={newShipment.quantity}
                      onChange={(e) => setNewShipment({ ...newShipment, quantity: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="20000"
                      min="1"
                      max={selectedShipmentRemaining || undefined}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Remaining allocation: {selectedShipmentRemaining.toLocaleString()} units
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Ship Date</label>
                    <input
                      type="date"
                      value={newShipment.estimated_ship_date}
                      onChange={(e) => setNewShipment({ ...newShipment, estimated_ship_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Arrival Date</label>
                    <input
                      type="date"
                      value={newShipment.estimated_arrival_date}
                      onChange={(e) => setNewShipment({ ...newShipment, estimated_arrival_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCreatePartialShipment}
                    disabled={savingManualShipment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {savingManualShipment ? 'Creating...' : 'Create Shipment'}
                  </button>
                  <button
                    onClick={resetNewShipmentForm}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Orders In Progress Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {ordersInProgress.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No orders in progress. Add an order manually to track it.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          SKU
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date Placed
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cost
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Allocations
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ordersInProgress.map((order) => (
                        <tr key={order.order_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {order.order_number ? (
                              order.order_number
                            ) : (
                              <span className="flex items-center gap-2 text-gray-400">
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading...
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.product_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {order.sku}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                            {order.quantity.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(order.date_ordered)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                            {order.total_cost ? `$${order.total_cost.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {order.allocations.map((alloc, i) => (
                              <div key={i} className="text-xs">
                                {alloc.warehouse}: {alloc.final_quantity.toLocaleString()} allocated, {getAllocationShippedQuantity(order.order_id, alloc).toLocaleString()} shipped, {getAllocationRemainingQuantity(order.order_id, alloc).toLocaleString()} remaining
                              </div>
                            ))}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-6">
                              {/* Primary Action: Ship */}
                              <button
                                onClick={() => handleOpenShipmentForm(order.order_id)}
                                disabled={!order.order_number}
                                className={`px-3 py-1.5 rounded-md font-medium transition-colors ${!order.order_number
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                  }`}
                              >
                                {!order.order_number ? (
                                  <span className="flex items-center gap-1">
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                  </span>
                                ) : 'Add Shipment'}
                              </button>

                              {order.order_number && (
                                <button
                                  onClick={() => handleEditOrder(order.order_id)}
                                  disabled={loadingOrderDetails}
                                  className={`text-xs ${loadingOrderDetails ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                                  title="Edit order quantity and allocations"
                                >
                                  Edit
                                </button>
                              )}

                              {/* Secondary Action: Delete (smaller, less prominent) */}
                              {order.order_number && <button
                                onClick={() => handleDeleteOrder(order.order_id)}
                                disabled={deletingOrderId === order.order_id}
                                className={`text-xs ${deletingOrderId === order.order_id
                                  ? 'text-gray-300 cursor-not-allowed'
                                  : 'text-gray-400 hover:text-red-600'
                                  }`}
                                title="Delete order"
                              >
                                {deletingOrderId === order.order_id ? (
                                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                              </button>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )
      }

      {
        !loadingBigQuery && !loadingOrders && !error && ordersToPlace.length === 0 && activeTab === 'orders-to-place' && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
            No urgent orders found. All products are adequately stocked.
          </div>
        )
      }

      {/* ============ SHIPMENTS TAB ============ - loads from Supabase  */}
      {
        activeTab === 'shipments' && !loadingOrders && (
          <div className="space-y-5">
            {/* Header Bar */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Shipment Tracking</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Track and manage inventory shipments across all warehouses</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => refreshSupabaseData()}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md flex items-center gap-1.5 transition-colors border border-gray-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={() => setActiveTab('in-progress')}
                      className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1.5 font-medium transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      New Shipment
                    </button>
                  </div>
                </div>
              </div>

              {/* Status Filter Tabs */}
              <div className="px-6 py-2 flex items-center gap-1 overflow-x-auto">
                <button
                  onClick={() => setActiveShipmentFilter('all')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${activeShipmentFilter === 'all' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50 transition-colors'}`}
                >
                  All ({shipmentStats.total})
                </button>
                <button
                  onClick={() => setActiveShipmentFilter('pending')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${activeShipmentFilter === 'pending' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50 transition-colors'}`}
                >
                  Pending ({shipmentStats.pending})
                </button>
                <button
                  onClick={() => setActiveShipmentFilter('in_transit')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${activeShipmentFilter === 'in_transit' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50 transition-colors'}`}
                >
                  In Transit ({shipmentStats.inTransit})
                </button>
                <button
                  onClick={() => setActiveShipmentFilter('customs')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${activeShipmentFilter === 'customs' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50 transition-colors'}`}
                >
                  Customs Clearance ({shipmentStats.customs})
                </button>
                <button
                  onClick={() => setActiveShipmentFilter('delivered')}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${activeShipmentFilter === 'delivered' ? 'text-blue-600 bg-blue-50' : 'text-gray-600 hover:bg-gray-50 transition-colors'}`}
                >
                  Delivered ({shipmentStats.delivered})
                </button>
              </div>
              <div className="px-6 pb-4">
                <input
                  type="text"
                  value={shipmentSearchQuery}
                  onChange={(e) => setShipmentSearchQuery(e.target.value)}
                  placeholder="Search by order, SKU, tracking number, or reference"
                  className="w-full md:w-[440px] px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                />
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-gray-900">{shipmentStats.total}</div>
                    <div className="text-xs text-gray-500">Total Shipments</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-amber-600">
                      {shipmentStats.pending}
                    </div>
                    <div className="text-xs text-gray-500">Pending</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-blue-600">
                      {shipmentStats.inTransit}
                    </div>
                    <div className="text-xs text-gray-500">In Transit</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-2xl font-semibold text-green-600">
                      {shipmentStats.delivered}
                    </div>
                    <div className="text-xs text-gray-500">Delivered</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipments Table - GROUPED BY ORDER NUMBER */}
            {filteredShipments.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  {shipments.length === 0 ? 'No shipments found' : 'No matching shipments'}
                </h3>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  {shipments.length === 0
                    ? 'Create an order and add shipments from its remaining allocations to start tracking inventory movement.'
                    : 'Try changing status tabs or clearing search text.'}
                </p>
                {shipments.length === 0 ? (
                  <button
                    onClick={() => setActiveTab('in-progress')}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-medium transition-colors"
                  >
                    Go to Orders
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setActiveShipmentFilter('all');
                      setShipmentSearchQuery('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 font-medium transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Table */}
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider w-8"></th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Order / Shipment</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Route</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Quantity</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Supplier</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">ETA</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedShipments.map((group) => (
                      <React.Fragment key={group.orderNumber}>
                        {/* Order Header Row */}
                        <tr
                          className="bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => toggleOrderExpand(group.orderNumber)}
                        >
                          <td className="px-4 py-3">
                            <svg
                              className={`w-5 h-5 text-gray-400 transition-transform ${expandedOrderNumbers.has(group.orderNumber) ? 'rotate-90' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-md bg-blue-100 text-blue-600 flex items-center justify-center">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm font-bold text-gray-900">📦 {group.orderNumber}</div>
                                <div className="text-xs text-gray-500">{group.shipments.length} shipment{group.shipments.length !== 1 ? 's' : ''} · <span className="text-blue-600 font-medium">{group.sku}</span></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {group.destinations.map(dest => (
                                <span key={dest} className="px-2 py-0.5 bg-gray-200 rounded text-xs font-medium text-gray-700">
                                  {dest}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-bold text-gray-900">{group.totalQuantity.toLocaleString()}</span>
                            <span className="text-xs text-gray-400 ml-1">units</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-700">{group.supplier || '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {group.statuses.map((status, idx) => (
                                <span key={idx} className={`px-2 py-0.5 rounded text-xs font-medium ${status === 'complete' || status === 'arrived_at_warehouse'
                                  ? 'bg-green-100 text-green-700'
                                  : status === 'in_transit'
                                    ? 'bg-blue-100 text-blue-700'
                                    : status === 'customs_clearance'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-amber-100 text-amber-700'
                                  }`}>
                                  {status.replace(/_/g, ' ').charAt(0).toUpperCase() + status.replace(/_/g, ' ').slice(1)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-gray-500">
                              {group.earliest_eta
                                ? new Date(group.earliest_eta).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const orderId = Number(group.order_id);
                                  if (Number.isFinite(orderId) && orderId > 0) {
                                    handleEditOrder(orderId);
                                  }
                                }}
                                disabled={loadingOrderDetails || !group.order_id}
                                className={`text-xs ${loadingOrderDetails || !group.order_id ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                                title="Edit order quantity and allocations"
                              >
                                Edit
                              </button>
                              <span className="text-xs text-gray-400">Click to expand</span>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Shipments */}
                        {expandedOrderNumbers.has(group.orderNumber) && group.shipments.map((shipment) => (
                          <React.Fragment key={shipment.shipment_id}>
                            <tr
                              className={`hover:bg-gray-50 cursor-pointer transition-colors border-l-4 border-l-blue-200 ${expandedShipment === shipment.shipment_id ? 'bg-blue-50' : 'bg-blue-50/30'}`}
                              onClick={() => setExpandedShipment(expandedShipment === shipment.shipment_id ? null : shipment.shipment_id)}
                            >
                              {/* Indent */}
                              <td className="px-4 py-4"></td>

                              {/* Shipment Info */}
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3 pl-4">
                                  <span className="text-gray-300">└─</span>
                                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${shipment.status === 'complete' || shipment.status === 'arrived_at_warehouse'
                                    ? 'bg-green-100 text-green-600'
                                    : shipment.status === 'in_transit'
                                      ? 'bg-blue-100 text-blue-600'
                                      : shipment.status === 'customs_clearance'
                                        ? 'bg-purple-100 text-purple-600'
                                        : 'bg-amber-100 text-amber-600'
                                    }`}>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">SHP-{shipment.shipment_id.toString().padStart(4, '0')}</div>
                                    <div className="text-xs text-gray-500">{shipment.sku}</div>
                                  </div>
                                </div>
                              </td>

                              {/* Route */}
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-700">{shipment.origin}</span>
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                  </svg>
                                  <span className="text-sm font-semibold text-gray-900">{shipment.destination}</span>
                                </div>
                                {shipment.shipping_method && (
                                  <div className="text-xs text-gray-400 mt-0.5">{shipment.shipping_method}</div>
                                )}
                              </td>

                              {/* Quantity */}
                              <td className="px-4 py-4">
                                <span className="text-sm font-semibold text-gray-900">{shipment.quantity.toLocaleString()}</span>
                                <span className="text-xs text-gray-400 ml-1">units</span>
                              </td>

                              {/* Supplier */}
                              <td className="px-4 py-4">
                                <div className="text-sm text-gray-700">{shipment.supplier || '—'}</div>
                                {shipment.tracking_number && (
                                  <div className="mt-0.5 flex items-center gap-2">
                                    <div className="text-xs text-blue-600 font-mono">{shipment.tracking_number}</div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(shipment.tracking_number as string, 'Tracking Number');
                                      }}
                                      className="px-1.5 py-0.5 text-[10px] border border-gray-200 rounded text-gray-600 hover:bg-gray-50"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                )}
                              </td>

                              {/* Status */}
                              <td className="px-4 py-4">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${shipment.status === 'complete' || shipment.status === 'arrived_at_warehouse'
                                      ? 'bg-green-500'
                                      : shipment.status === 'in_transit'
                                        ? 'bg-blue-500 animate-pulse'
                                        : shipment.status === 'customs_clearance'
                                          ? 'bg-purple-500'
                                          : 'bg-amber-500'
                                      }`}></span>
                                    <span className={`text-sm font-medium ${shipment.status === 'complete' || shipment.status === 'arrived_at_warehouse'
                                      ? 'text-green-700'
                                      : shipment.status === 'in_transit'
                                        ? 'text-blue-700'
                                        : shipment.status === 'customs_clearance'
                                          ? 'text-purple-700'
                                          : 'text-amber-700'
                                      }`}>
                                      {shipment.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                    </span>
                                  </div>
                                  <div className="flex flex-wrap gap-1">
                                    {isTrackingMissingForShipment(shipment) && (
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                                        Missing Tracking
                                      </span>
                                    )}
                                    {isShipmentOverdue(shipment) && (
                                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
                                        ETA Overdue
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* ETA */}
                              <td className="px-4 py-4">
                                <div className={`text-sm ${isShipmentOverdue(shipment) ? 'text-orange-700 font-medium' : 'text-gray-700'}`}>
                                  {shipment.estimated_arrival_date
                                    ? new Date(shipment.estimated_arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                    : '—'}
                                </div>
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-4 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewShipmentLog(shipment); }}
                                    className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                    title="View History"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEditShipment(shipment); setExpandedShipment(shipment.shipment_id); }}
                                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteShipment(shipment); }}
                                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                  <svg
                                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedShipment === shipment.shipment_id ? 'rotate-180' : ''}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </div>
                              </td>
                            </tr>

                            {/* Expanded Row */}
                            {expandedShipment === shipment.shipment_id && (
                              <tr>
                                <td colSpan={8} className="px-0 py-0">
                                  {/* Progress Timeline */}
                                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                    <div className="flex items-center justify-between relative max-w-3xl mx-auto">
                                      {/* Progress Line Background */}
                                      <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200"></div>
                                      {/* Progress Line Fill */}
                                      <div
                                        className="absolute top-4 left-0 h-0.5 bg-blue-500 transition-all duration-500"
                                        style={{ width: `${(getStatusIndex(shipment.status) / (getStatusSteps().length - 1)) * 100}%` }}
                                      ></div>

                                      {getStatusSteps().map((step, idx) => {
                                        const currentIdx = getStatusIndex(shipment.status);
                                        const isComplete = idx < currentIdx;
                                        const isCurrent = idx === currentIdx;
                                        return (
                                          <div key={step.key} className="flex-1 relative z-10">
                                            <div className="flex flex-col items-center">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-all ${isComplete
                                                ? 'bg-blue-500 border-blue-500 text-white'
                                                : isCurrent
                                                  ? 'bg-white border-blue-500 text-blue-600'
                                                  : 'bg-white border-gray-300 text-gray-400'
                                                }`}>
                                                {isComplete ? (
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                  </svg>
                                                ) : idx + 1}
                                              </div>
                                              <span className={`text-xs mt-1.5 font-medium ${isComplete || isCurrent ? 'text-gray-700' : 'text-gray-400'
                                                }`}>
                                                {step.label}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>

                                  {/* Expanded Content */}
                                  <div className="bg-white border-t border-gray-100">
                                    {editingShipment?.shipment_id === shipment.shipment_id ? (
                                      /* Edit Mode */
                                      <div className="p-6 space-y-5">
                                        <div className="flex items-center justify-between">
                                          <h4 className="text-sm font-semibold text-gray-900">Update Shipment</h4>
                                          <button
                                            onClick={() => {
                                              setShipmentFormErrors([]);
                                              setStatusDropdownOpen(false);
                                              setShippingMethodDropdownOpen(false);
                                              setEditingShipment(null);
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                          >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
                                            <div ref={statusDropdownRef} className="relative">
                                              <button
                                                type="button"
                                                onClick={() => setStatusDropdownOpen((prev) => !prev)}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white text-left flex items-center justify-between focus:outline-none focus:ring-0 focus:border-blue-500"
                                              >
                                                <span className="text-gray-900">
                                                  {getStatusSteps().find((s) => s.key === editingShipment.status)?.label || editingShipment.status}
                                                </span>
                                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                              </button>
                                              {statusDropdownOpen && (
                                                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1">
                                                  {getStatusSteps().map((step) => (
                                                    <button
                                                      key={`status-${step.key}`}
                                                      type="button"
                                                      onClick={() => {
                                                        setEditingShipment({ ...editingShipment, status: step.key });
                                                        setStatusDropdownOpen(false);
                                                      }}
                                                      className={`w-full px-3 py-2 text-sm text-left transition-colors ${editingShipment.status === step.key
                                                        ? 'bg-blue-50 text-blue-700 font-medium'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                      {step.label}
                                                    </button>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                              Supplier
                                              <span className="text-gray-400 font-normal ml-1 text-xs">
                                                ({(editingShipment.supplier || '').length}/100)
                                              </span>
                                            </label>
                                            <input
                                              type="text"
                                              value={editingShipment.supplier || ''}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length <= 100) {
                                                  setEditingShipment({ ...editingShipment, supplier: value });
                                                }
                                              }}
                                              placeholder="Factory name"
                                              maxLength={100}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Shipping Method</label>
                                            <div ref={shippingMethodDropdownRef} className="relative">
                                              <button
                                                type="button"
                                                onClick={() => setShippingMethodDropdownOpen((prev) => !prev)}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-white text-left flex items-center justify-between focus:outline-none focus:ring-0 focus:border-blue-500"
                                              >
                                                <span className="text-gray-900">
                                                  {editingShipment.shipping_method || 'Air Freight'}
                                                </span>
                                                <svg className={`w-4 h-4 text-gray-400 transition-transform ${shippingMethodDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                              </button>
                                              {shippingMethodDropdownOpen && (
                                                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg py-1">
                                                  {['Air Freight', 'Sea Freight', 'Sea Express', 'Rail'].map((methodOption) => (
                                                    <button
                                                      key={`method-${methodOption}`}
                                                      type="button"
                                                      onClick={() => {
                                                        setEditingShipment({ ...editingShipment, shipping_method: methodOption });
                                                        setShippingMethodDropdownOpen(false);
                                                      }}
                                                      className={`w-full px-3 py-2 text-sm text-left transition-colors flex items-center justify-between ${
                                                        (editingShipment.shipping_method || '').trim().toLowerCase() === methodOption.toLowerCase()
                                                          ? 'bg-blue-50 text-blue-700 font-medium'
                                                          : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                      {methodOption}
                                                      {(editingShipment.shipping_method || '').trim().toLowerCase() === methodOption.toLowerCase() && (
                                                        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                      )}
                                                    </button>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Est. Ship Date</label>
                                            <input
                                              type="date"
                                              value={editingShipment.estimated_ship_date || ''}
                                              onChange={(e) => setEditingShipment({ ...editingShipment, estimated_ship_date: e.target.value })}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Actual Ship Date</label>
                                            <input
                                              type="date"
                                              value={editingShipment.actual_ship_date || ''}
                                              onChange={(e) => setEditingShipment({ ...editingShipment, actual_ship_date: e.target.value })}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Est. Arrival</label>
                                            <input
                                              type="date"
                                              value={editingShipment.estimated_arrival_date || ''}
                                              onChange={(e) => setEditingShipment({ ...editingShipment, estimated_arrival_date: e.target.value })}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Actual Arrival</label>
                                            <input
                                              type="date"
                                              value={editingShipment.actual_arrival_date || ''}
                                              onChange={(e) => setEditingShipment({ ...editingShipment, actual_arrival_date: e.target.value })}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                        </div>

                                        <div>
                                          {shipmentFormErrors.length > 0 && (
                                            <div className="mb-3 p-3 rounded-md border border-red-200 bg-red-50">
                                              <div className="text-xs font-semibold text-red-700 mb-1">Please fix the following:</div>
                                              <ul className="text-xs text-red-700 space-y-0.5">
                                                {shipmentFormErrors.map((error, index) => (
                                                  <li key={`shipment-error-${index}`}>- {error}</li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          <div className="flex items-center justify-between mb-2">
                                            <label className="block text-xs font-medium text-gray-600">
                                              Tracking Entries
                                            </label>
                                            <button
                                              type="button"
                                              onClick={addTrackingEntry}
                                              className="px-2.5 py-1 text-xs border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50"
                                            >
                                              Add Tracking
                                            </button>
                                          </div>
                                          <div className="space-y-3">
                                            {(editingShipment.tracking_entries || []).map((entry, index) => (
                                              <div key={`${editingShipment.shipment_id}-tracking-${index}`} className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                                <div className="md:col-span-4">
                                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Tracking Number
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={entry.tracking_number || ''}
                                                    onChange={(e) => {
                                                      const value = e.target.value;
                                                      if (value.length <= 100) {
                                                        updateTrackingEntry(index, 'tracking_number', value);
                                                      }
                                                    }}
                                                    placeholder="TRACK123456"
                                                    maxLength={100}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500 font-mono"
                                                  />
                                                </div>
                                                <div className="md:col-span-5">
                                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Tracking URL
                                                  </label>
                                                  <input
                                                    type="url"
                                                    value={entry.tracking_url || ''}
                                                    onChange={(e) => {
                                                      const value = e.target.value;
                                                      if (value.length <= 500) {
                                                        updateTrackingEntry(index, 'tracking_url', value);
                                                      }
                                                    }}
                                                    placeholder="https://tracking.example.com"
                                                    maxLength={500}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                                  />
                                                </div>
                                                <div className="md:col-span-2">
                                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                                    Cartons
                                                  </label>
                                                  <input
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    value={entry.cartons_count ?? ''}
                                                    onChange={(e) => {
                                                      const value = e.target.value;
                                                      if (value === '') {
                                                        updateTrackingEntry(index, 'cartons_count', null);
                                                        return;
                                                      }
                                                      const parsed = Number(value);
                                                      if (Number.isInteger(parsed) && parsed >= 0) {
                                                        updateTrackingEntry(index, 'cartons_count', parsed);
                                                      }
                                                    }}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                                  />
                                                </div>
                                                <div className="md:col-span-1 flex items-end">
                                                  <button
                                                    type="button"
                                                    onClick={() => removeTrackingEntry(index)}
                                                    className="w-full px-2 py-2 text-xs border border-red-200 text-red-700 rounded-md hover:bg-red-50"
                                                  >
                                                    Remove
                                                  </button>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                              Reference / BOL
                                              <span className="text-gray-400 font-normal ml-1 text-xs">
                                                ({(editingShipment.shipping_reference || '').length}/100)
                                              </span>
                                            </label>
                                            <input
                                              type="text"
                                              value={editingShipment.shipping_reference || ''}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length <= 100) {
                                                  setEditingShipment({ ...editingShipment, shipping_reference: value });
                                                }
                                              }}
                                              placeholder="BL-2024-001"
                                              maxLength={100}
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes</label>
                                            <input
                                              type="text"
                                              value={editingShipment.notes || ''}
                                              onChange={(e) => setEditingShipment({ ...editingShipment, notes: e.target.value })}
                                              placeholder="Any additional notes..."
                                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-0 focus:border-blue-500"
                                            />
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-2 pt-2">
                                          <button
                                            onClick={handleSaveShipment}
                                            disabled={savingShipment}
                                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center gap-2"
                                          >
                                            {savingShipment ? (
                                              <>
                                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                Saving...
                                              </>
                                            ) : 'Save Changes'}
                                          </button>
                                          <button
                                            onClick={() => {
                                              setShipmentFormErrors([]);
                                              setStatusDropdownOpen(false);
                                              setShippingMethodDropdownOpen(false);
                                              setEditingShipment(null);
                                            }}
                                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium"
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      /* View Mode */
                                      <div className="p-6">
                                        <div className="grid grid-cols-2 md:grid-cols-7 gap-6">
                                          <div>
                                            <div className="text-xs text-gray-500 mb-1">Supplier</div>
                                            <div className="text-sm font-medium text-gray-900">{shipment.supplier || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-gray-500 mb-1">Shipping Method</div>
                                            <div className="text-sm font-medium text-gray-900">{shipment.shipping_method || '—'}</div>
                                          </div>
                                          <div className="md:col-span-2">
                                            <div className="text-xs text-gray-500 mb-1">Tracking Entries</div>
                                            <div className="space-y-1.5">
                                              {getShipmentTrackingEntries(shipment).length > 0 ? (
                                                getShipmentTrackingEntries(shipment).map((entry, index) => (
                                                  <div key={`${shipment.shipment_id}-view-tracking-${index}`} className="text-sm">
                                                    <div className="flex items-center gap-2">
                                                      <div className="font-medium font-mono text-blue-600 break-all">{entry.tracking_number}</div>
                                                      <button
                                                        type="button"
                                                        onClick={() => copyToClipboard(entry.tracking_number, 'Tracking Number')}
                                                        className="px-1.5 py-0.5 text-[10px] border border-gray-200 rounded text-gray-600 hover:bg-gray-50"
                                                      >
                                                        Copy
                                                      </button>
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                                      {entry.tracking_url ? (
                                                        <>
                                                          <a
                                                            href={entry.tracking_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 break-all"
                                                          >
                                                            {entry.tracking_url}
                                                          </a>
                                                          <button
                                                            type="button"
                                                            onClick={() => copyToClipboard(entry.tracking_url as string, 'Tracking URL')}
                                                            className="px-1.5 py-0.5 text-[10px] border border-gray-200 rounded text-gray-600 hover:bg-gray-50"
                                                          >
                                                            Copy
                                                          </button>
                                                        </>
                                                      ) : (
                                                        <span className="text-gray-400">No URL</span>
                                                      )}
                                                      {entry.cartons_count !== null && entry.cartons_count !== undefined && (
                                                        <span>{entry.cartons_count} ctns</span>
                                                      )}
                                                    </div>
                                                  </div>
                                                ))
                                              ) : (
                                                <span className="text-sm text-gray-400">—</span>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-gray-500 mb-1">Reference / BOL</div>
                                            <div className="text-sm font-medium text-gray-900">{shipment.shipping_reference || '—'}</div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-gray-500 mb-1">Ship Date</div>
                                            <div className="text-sm">
                                              {shipment.actual_ship_date ? (
                                                <span className="font-medium text-green-600">
                                                  {new Date(shipment.actual_ship_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                  <span className="text-xs text-gray-400 ml-1">(actual)</span>
                                                </span>
                                              ) : shipment.estimated_ship_date ? (
                                                <span className="font-medium text-gray-700">
                                                  {new Date(shipment.estimated_ship_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                  <span className="text-xs text-gray-400 ml-1">(est.)</span>
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">Not set</span>
                                              )}
                                            </div>
                                          </div>
                                          <div>
                                            <div className="text-xs text-gray-500 mb-1">Arrival Date</div>
                                            <div className="text-sm">
                                              {shipment.actual_arrival_date ? (
                                                <span className="font-medium text-green-600">
                                                  {new Date(shipment.actual_arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                  <span className="text-xs text-gray-400 ml-1">(actual)</span>
                                                </span>
                                              ) : shipment.estimated_arrival_date ? (
                                                <span className="font-medium text-gray-700">
                                                  {new Date(shipment.estimated_arrival_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                  <span className="text-xs text-gray-400 ml-1">(est.)</span>
                                                </span>
                                              ) : (
                                                <span className="text-gray-400">Not set</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>

                                        {shipment.notes && (
                                          <div className="mt-4 px-3 py-2 bg-amber-50 rounded-md border border-amber-100">
                                            <span className="text-xs text-amber-700 font-medium">Note:</span>
                                            <span className="text-sm text-amber-800 ml-2">{shipment.notes}</span>
                                          </div>
                                        )}

                                        <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                                          <span>Created {new Date(shipment.created_at).toLocaleDateString()}</span>
                                          <button
                                            onClick={() => handleEditShipment(shipment)}
                                            className="text-blue-600 hover:text-blue-800 font-medium"
                                          >
                                            Edit Details →
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      }


      {/* Shipment Change History Modal */}
      {
        showLogModal && selectedShipmentForLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-gray-800 font-semibold text-lg">
                      Tracking History
                    </h3>
                    <p className="text-gray-500 text-sm mt-0.5">
                      {selectedShipmentForLog.sku} • {selectedShipmentForLog.origin || 'CN'} → {selectedShipmentForLog.destination}
                    </p>
                  </div>
                  <button
                    onClick={() => { setShowLogModal(false); setSelectedShipmentForLog(null); setShipmentLogs([]); }}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Tracking Number */}
                <div className="mt-3 bg-gray-100 border border-gray-200 rounded px-3 py-2 inline-flex items-center gap-2">
                  <span className="text-gray-500 text-xs">TRACKING #</span>
                  <span className="text-gray-800 font-mono font-semibold">
                    SHP-{selectedShipmentForLog.shipment_id.toString().padStart(4, '0')}
                  </span>
                </div>
              </div>

              {/* Timeline Content */}
              <div className="overflow-y-auto max-h-[55vh]">
                {loadingLogs ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
                  </div>
                ) : shipmentLogs.length === 0 ? (
                  <div className="text-center py-16 px-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <p className="text-gray-600 font-medium">No tracking information available</p>
                    <p className="text-gray-400 text-sm mt-1">Updates will appear here</p>
                  </div>
                ) : (
                  <div className="px-5 py-4">
                    {shipmentLogs.map((log, index) => {
                      const action = formatAction(log.action);
                      const isFirst = index === 0;
                      const isLast = index === shipmentLogs.length - 1;

                      return (
                        <div key={log.log_id || index} className="flex">
                          {/* Left side - Date/Time */}
                          <div className="w-24 flex-shrink-0 text-right pr-4 pt-0.5">
                            <div className="text-xs font-medium text-gray-900">
                              {log.changed_at ? (() => {
                                // Backend sends UTC time - ensure proper parsing by adding 'Z' if missing
                                const utcDate = log.changed_at.endsWith('Z') ? log.changed_at : `${log.changed_at}Z`;
                                return new Date(utcDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                });
                              })() : '—'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {log.changed_at ? (() => {
                                // Backend sends UTC time - ensure proper parsing by adding 'Z' if missing
                                const utcDate = log.changed_at.endsWith('Z') ? log.changed_at : `${log.changed_at}Z`;
                                const date = new Date(utcDate);
                                const timeStr = date.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                });
                                // Get timezone abbreviation (e.g., "IST", "EST")
                                const tzShort = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
                                return `${timeStr} ${tzShort}`;
                              })() : ''}
                            </div>
                          </div>

                          {/* Timeline connector */}
                          <div className="flex flex-col items-center mr-4">
                            {/* Circle */}
                            <div className={`
                            w-3 h-3 rounded-full flex-shrink-0 border-2
                            ${isFirst
                                ? 'bg-green-600 border-green-600'
                                : 'bg-white border-gray-300'
                              }
                          `}>
                              {isFirst && (
                                <svg className="w-full h-full text-white" viewBox="0 0 12 12" fill="currentColor">
                                  <path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="scale(0.6) translate(4,4)" />
                                </svg>
                              )}
                            </div>
                            {/* Line */}
                            {!isLast && (
                              <div className="w-0.5 flex-1 bg-gray-200 min-h-[50px]"></div>
                            )}
                          </div>

                          {/* Content */}
                          <div className={`flex-1 pb-6 ${isLast ? 'pb-2' : ''}`}>
                            {/* Status Badge */}
                            <div className={`
                            inline-block text-xs font-semibold px-2 py-0.5 rounded mb-1
                            ${isFirst
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700'
                              }
                          `}>
                              {action.label}
                            </div>

                            {/* Description */}
                            {log.action === 'created' ? (
                              <p className="text-sm text-gray-800 mt-1">{log.new_value}</p>
                            ) : (
                              <div className="mt-1">
                                <p className="text-sm text-gray-800 font-medium">
                                  {formatFieldName(log.field_name)}
                                </p>
                                <div className="mt-1 text-sm">
                                  <span className="text-gray-400 line-through">{log.old_value || '(empty)'}</span>
                                  <span className="mx-2 text-gray-400">→</span>
                                  <span className="text-green-600 font-medium">{log.new_value || '(empty)'}</span>
                                </div>
                              </div>
                            )}

                            {/* User */}
                            <p className="text-xs text-gray-400 mt-1">
                              Updated by {log.changed_by || 'system'}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {shipmentLogs.length} update{shipmentLogs.length !== 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => { setShowLogModal(false); setSelectedShipmentForLog(null); setShipmentLogs([]); }}
                  className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

export default UnifiedOrderManagement;
